import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toPng } from "html-to-image";
import CodeMirror from "@uiw/react-codemirror";
import { vscodeDark, vscodeLight } from "@uiw/codemirror-theme-vscode";
import { extractDiagnostics } from "../../utils/codeMirrorExtensions";
import NebulaCanvasControls, { NEBULA_BTN_CLS, NEBULA_BTN_ACTIVE_CLS } from "./NebulaCanvasControls";
import NebulaPreviewGate from "./NebulaPreviewGate";
import MarkdownRenderer from "../Markdown/MarkdownRenderer";
import ReactViz from "../Markdown/ReactViz";
import PhysicsViz from "../Markdown/PhysicsViz";
import MermaidDiagram from "../Markdown/MermaidDiagram";
import MarkmapDiagram from "../Markdown/MarkmapDiagram";
import { transformContent } from "../../utils/helpers";
import useDownloadFile from "../../hooks/useDownloadFile";
import useNativeFullscreen from "../../hooks/useNativeFullscreen";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import useArtifactSave from "../../hooks/useArtifactSave";
import useInlineEdit from "../../hooks/useInlineEdit";
import useInlineEditAttachments from "../../hooks/useInlineEditAttachments";
import FileUploadItem from "../Prompt/FileUploadItem";
import { useSparkContext } from "../../context/SparkContext";
import EditorModelSelector from "./EditorModelSelector";
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import ChatStore from "../../store/chatStore";
import useStreamingStateStore from "../../store/useStreamingStateStore";
import { diffArtifacts, hasDiff, getDiffLineMap } from "../../utils/nebulaArtifactDiff";
import { groupIntoHunks, buildDisplayedCode, getHunkDisplayMap } from "../../utils/nebulaArtifactHunks";
import { getCaps } from "../../utils/nebulaArtifactTypes";
import { getCmExtensions } from "../../utils/codeMirrorExtensions";
import { buildDiffExtension } from "../../utils/codemirrorDiffExtension";
import { EditorView } from "@codemirror/view";
import { buildUpdatedNebulaContent } from "../../utils/nebulaContentRebuild";
import { SaveButton, ResetButton, PreviewCodeToggle } from "../shared/EditorControls";
import VizCopyButton from "../shared/Viz/VizCopyButton";
import { prepareHtml } from "../../utils/nebulaHtml";
import useResolvedMediaUrl from "../../hooks/useResolvedMediaUrl";
import useResolvedMediaUrlStore from "../../store/useResolvedMediaUrlStore";
import UserAuthStore from "../../store/userStore";
import { ArrowUp, Square, X, Check, GitCompare, History, Pencil, Wand2, Paperclip, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { tokenizeHtmlForEditing, buildPatchedHtml } from "../../utils/nebulaTextTokenizer";
import useArtifactVersions from "../../hooks/useArtifactVersions";
import useArtifactEditSignal from "../../store/useArtifactEditSignal";
import ArtifactVersionHistory from "./ArtifactVersionHistory";
import AnchoredPanel from "../shared/AnchoredPanel/AnchoredPanel";
import { parseNebulaResponse } from "../../utils/nebulaParser";

// Extract the inner artifact code from a version's stored content.
// Saved versions hold the full answer string (prose + ```react/```html/etc.
// fenced block); browsers/runners need just the inner code. If parsing fails
// or yields plain "text", fall back to the raw content so the user still sees
// something rather than a hard error.
function extractArtifactCode(raw, itemIndex = 0) {
  if (!raw) return raw;
  const parsed = parseNebulaResponse(raw);
  if (parsed && parsed.type !== "text") {
    const items = parsed.items;
    if (Array.isArray(items) && items[itemIndex]) {
      return items[itemIndex].content;
    }
    return parsed.content;
  }
  return raw;
}

const ARTIFACT_TYPE_LABELS = {
  html:    "Web",
  react:   "Component",
  plotly:  "Chart",
  mermaid: "Diagram",
  markmap: "Mindmap",
  image:   "Image",
  video:   "Video",
  audio:   "Audio",
  text:    "Text",
};

// Compact switcher for multiple artifacts within a single turn (tabs + prev/next).
function MultiArtifactPager({ items = [], activeIndex = 0, onSelect }) {
  if (!items || items.length <= 1) return null;
  const total = items.length;
  const safeIndex = Math.min(Math.max(0, activeIndex), total - 1);

  // Build readable per-tab labels (e.g. "Image 1", "Image 2" or "Chart 1", "Diagram 1")
  const typeTotals = {};
  items.forEach((it) => { typeTotals[it.type] = (typeTotals[it.type] || 0) + 1; });
  const seenCounts = {};
  const labels = items.map((it) => {
    const base = ARTIFACT_TYPE_LABELS[it.type] || "Item";
    seenCounts[it.type] = (seenCounts[it.type] || 0) + 1;
    return typeTotals[it.type] > 1 ? `${base} ${seenCounts[it.type]}` : base;
  });

  return (
    <div className="flex items-center gap-1">
      {total <= 6 ? (
        <div className="flex items-center rounded-lg border border-edge-gray-200 dark:border-edge-panel-surface bg-fill-gray-50 dark:bg-fill-panel-surface p-0.5 gap-0.5">
          {items.map((_, idx) => {
            const active = idx === safeIndex;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => onSelect(idx)}
                className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors whitespace-nowrap ${
                  active
                    ? "bg-fill-white dark:bg-fill-editor-surface text-ink-ink dark:text-ink-white shadow-sm"
                    : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-white"
                }`}
              >
                {labels[idx]}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => onSelect((safeIndex - 1 + total) % total)}
          title="Previous item (←)"
          aria-label="Previous item"
          className={NEBULA_BTN_CLS}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="px-1.5 text-[11px] font-medium tabular-nums text-ink-gray-500 dark:text-ink-gray-400 select-none">
          {safeIndex + 1} / {total}
        </span>
        <button
          type="button"
          onClick={() => onSelect((safeIndex + 1) % total)}
          title="Next item (→)"
          aria-label="Next item"
          className={NEBULA_BTN_CLS}
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Thumbnail strip for multi-image galleries
function ImageFilmstripThumb({ item, index, isActive, onClick }) {
  const url = useResolvedMediaUrl(item.content, item.isStoragePath);
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Image ${index + 1}`}
      className={`relative w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border transition-all ${
        isActive
          ? "ring-2 ring-focus-canvas-accent border-edge-canvas-accent scale-105"
          : "border-edge-gray-200 dark:border-edge-panel-surface opacity-70 hover:opacity-100"
      }`}
    >
      {url ? (
        <img src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-fill-gray-100 dark:bg-fill-panel-surface flex items-center justify-center text-[10px] text-ink-gray-400">
          {index + 1}
        </div>
      )}
    </button>
  );
}

// ── Storage-path-aware image/video renderers ──────────────────────────────────
function CanvasImage({ artifact, items = [], activeItemIndex = 0, onSelectItem, hasReportContent = false, reportMode = false, onToggleReport, preamble = "", postamble = "", isGhosted, ghostedOverlay, changeBadgeOverlay = null, swapClass = "", statusPill = null, previewRef, enterFullscreen, isFullscreen, onDownload }) {
  const url = useResolvedMediaUrl(artifact.content, artifact.isStoragePath);
  const imageItems = items.filter((it) => it.type === "image");
  const showFilmstrip = imageItems.length > 1 && imageItems.length === items.length;
  return (
    <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 transition-opacity opacity-100 ${swapClass}`}>
      {!isFullscreen && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2 py-1 border-b border-edge-gray-200 dark:border-edge-panel-surface bg-fill-white dark:bg-fill-page-surface-dark">
          <div className="flex items-center gap-2 min-w-0">
            <MultiArtifactPager items={items} activeIndex={activeItemIndex} onSelect={onSelectItem} />
            {statusPill}
          </div>
          <NebulaCanvasControls
            artifact={artifact}
            canDiff={false}
            diffMode={false}
            hasPrevious={false}
            isFullscreen={isFullscreen}
            hasReportContent={hasReportContent}
            reportMode={reportMode}
            onToggleReport={onToggleReport}
            onFullscreen={enterFullscreen}
            onDownload={onDownload}
          />
        </div>
      )}
      {ghostedOverlay}{changeBadgeOverlay}
      {reportMode ? (
        <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
          <ReportTextBlock content={preamble} position="pre" />
          <div className="flex items-center justify-center p-8">
            {url
              ? <img src={url} alt="Generated" className="max-w-full max-h-[70vh] object-contain rounded-xl" />
              : <div className="text-ink-gray-400 text-sm">Loading…</div>
            }
          </div>
          <ReportTextBlock content={postamble} position="post" />
        </div>
      ) : (
        <div ref={previewRef} className="flex-1 min-h-0 flex flex-col items-center justify-center p-8 gap-4 overflow-hidden">
          <div className="flex-1 min-h-0 w-full flex items-center justify-center">
            {url
              ? <img src={url} alt="Generated" className="max-w-full max-h-full object-contain rounded-xl" />
              : <div className="text-ink-gray-400 text-sm">Loading…</div>
            }
          </div>
          {showFilmstrip && !isFullscreen && (
            <div className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-xl bg-fill-gray-50/90 dark:bg-fill-panel-surface/90 border border-edge-gray-200 dark:border-edge-panel-surface overflow-x-auto max-w-full">
              {items.map((it, idx) => (
                <ImageFilmstripThumb
                  key={`${it.content}-${idx}`}
                  item={it}
                  index={idx}
                  isActive={idx === activeItemIndex}
                  onClick={() => onSelectItem(idx)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CanvasVideo({ artifact, items = [], activeItemIndex = 0, onSelectItem, hasReportContent = false, reportMode = false, onToggleReport, preamble = "", postamble = "", isGhosted, ghostedOverlay, changeBadgeOverlay = null, swapClass = "", statusPill = null, previewRef, enterFullscreen, isFullscreen, onDownload }) {
  const url = useResolvedMediaUrl(artifact.content, artifact.isStoragePath);
  return (
    <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 transition-opacity opacity-100 ${swapClass}`}>
      {!isFullscreen && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2 py-1 border-b border-edge-gray-200 dark:border-edge-panel-surface bg-fill-white dark:bg-fill-page-surface-dark">
          <div className="flex items-center gap-2 min-w-0">
            <MultiArtifactPager items={items} activeIndex={activeItemIndex} onSelect={onSelectItem} />
            {statusPill}
          </div>
          <NebulaCanvasControls
            artifact={artifact}
            canDiff={false}
            diffMode={false}
            hasPrevious={false}
            isFullscreen={isFullscreen}
            hasReportContent={hasReportContent}
            reportMode={reportMode}
            onToggleReport={onToggleReport}
            onFullscreen={enterFullscreen}
            onDownload={onDownload}
          />
        </div>
      )}
      {ghostedOverlay}{changeBadgeOverlay}
      {reportMode ? (
        <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
          <ReportTextBlock content={preamble} position="pre" />
          <div className="flex items-center justify-center p-8">
            {url
              ? <video src={url} controls autoPlay loop className="max-w-full max-h-[70vh] rounded-xl" />
              : <div className="text-ink-gray-400 text-sm">Loading…</div>
            }
          </div>
          <ReportTextBlock content={postamble} position="post" />
        </div>
      ) : (
        <div ref={previewRef} className="flex-1 flex items-center justify-center p-8">
          {url
            ? <video src={url} controls autoPlay loop className="max-w-full max-h-full rounded-xl" />
            : <div className="text-ink-gray-400 text-sm">Loading…</div>
          }
        </div>
      )}
    </div>
  );
}

function CanvasAudio({ artifact, items = [], activeItemIndex = 0, onSelectItem, hasReportContent = false, reportMode = false, onToggleReport, preamble = "", postamble = "", isGhosted, ghostedOverlay, changeBadgeOverlay = null, swapClass = "", statusPill = null, previewRef, enterFullscreen, isFullscreen, onDownload }) {
  const url = useResolvedMediaUrl(artifact.content, artifact.isStoragePath);
  return (
    <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 transition-opacity opacity-100 ${swapClass}`}>
      {!isFullscreen && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2 py-1 border-b border-edge-gray-200 dark:border-edge-panel-surface bg-fill-white dark:bg-fill-page-surface-dark">
          <div className="flex items-center gap-2 min-w-0">
            <MultiArtifactPager items={items} activeIndex={activeItemIndex} onSelect={onSelectItem} />
            {statusPill}
          </div>
          <NebulaCanvasControls
            artifact={artifact}
            canDiff={false}
            diffMode={false}
            hasPrevious={false}
            isFullscreen={isFullscreen}
            hasReportContent={hasReportContent}
            reportMode={reportMode}
            onToggleReport={onToggleReport}
            onFullscreen={enterFullscreen}
            onDownload={onDownload}
          />
        </div>
      )}
      {ghostedOverlay}{changeBadgeOverlay}
      {reportMode ? (
        <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
          <ReportTextBlock content={preamble} position="pre" />
          <div className="flex items-center justify-center p-8">
            {url
              ? (
                  <div className="flex flex-col items-center gap-4 w-full max-w-lg">
                    <audio controls className="w-full rounded-lg">
                      <source src={url} />
                    </audio>
                  </div>
                )
              : <div className="text-ink-gray-400 text-sm">Loading…</div>
            }
          </div>
          <ReportTextBlock content={postamble} position="post" />
        </div>
      ) : (
        <div ref={previewRef} className="flex-1 flex items-center justify-center p-8">
          {url
            ? (
                <div className="flex flex-col items-center gap-4 w-full max-w-lg">
                  <audio controls className="w-full rounded-lg">
                    <source src={url} />
                  </audio>
                </div>
              )
            : <div className="text-ink-gray-400 text-sm">Loading…</div>
          }
        </div>
      )}
    </div>
  );
}

// ── Inline AI edit diff view — imported from shared ──────────────────────────

// ── Shared presentational helpers ────────────────────────────────────────────

const SplitDragHandle = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    title="Drag to resize"
    className="relative w-[5px] flex-shrink-0 cursor-col-resize group flex items-center justify-center"
  >
    <div className="h-full w-px bg-fill-gray-200/50 dark:bg-fill-panel-surface/70 group-hover:bg-fill-canvas-accent/40 group-active:bg-fill-canvas-accent transition-colors" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-9 rounded-full border bg-fill-gray-300 dark:bg-fill-toolbar-hover border-edge-gray-200 dark:border-edge-panel-surface group-hover:bg-fill-canvas-accent group-hover:border-edge-canvas-accent group-active:bg-fill-canvas-accent group-active:border-edge-canvas-accent transition-all pointer-events-none" />
  </div>
);

// Renders preamble (position="pre") or postamble (position="post") text in report layout.
// Returns null when content is empty so callers don't need to guard.
const ReportTextBlock = ({ content, position = "pre" }) => {
  if (!content) return null;
  return (
    <div className={`max-w-4xl w-full mx-auto px-8 text-[14px] leading-7 text-ink-hex-3d4f5c dark:text-ink-hex-c8d8e4 ${position === "pre" ? "pt-8 pb-4" : "pt-4 pb-8"}`}>
      <MarkdownRenderer content={content} />
    </div>
  );
};

const NebulaCanvas = ({ artifact, isGenerating = false, activityItems = [], previousArtifact = null, answerId = null, messageId = null, chatOwnerId = null, rawContent = null, isVisible = true }) => {
  const { theme, colorScheme } = useTheme();
  const cmTheme = theme === "dark" ? vscodeDark : vscodeLight;
  const { copied, copyToClipboard } = useCopyToClipboard({ caller: "NebulaCanvas" });
  const [viewMode, setViewMode] = useState("preview");
  const [diffMode, setDiffMode] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(0);

  // Reset active sub-item index when switching messages/answers
  useEffect(() => {
    setActiveItemIndex(0);
  }, [answerId, messageId]);

  const rawArtifactItems = useMemo(() => {
    if (Array.isArray(artifact?.items) && artifact.items.length > 0) {
      return artifact.items;
    }
    return artifact ? [artifact] : [];
  }, [artifact]);

  const clampedItemIndex = rawArtifactItems.length > 0
    ? Math.min(activeItemIndex, rawArtifactItems.length - 1)
    : 0;

  const activeArtifact = useMemo(() => {
    if (!artifact) return null;
    if (rawArtifactItems.length <= 1) return artifact;
    const item = rawArtifactItems[clampedItemIndex] || rawArtifactItems[0];
    return {
      ...artifact,
      ...item,
      items: rawArtifactItems,
    };
  }, [artifact, rawArtifactItems, clampedItemIndex]);

  // Editable artifact code state — `hasUserEdited` distinguishes real edits
  // from streaming prop updates so the editor stays in sync with newly
  // arriving content until the user actually types.
  //
  // When the canvas mounts mid-stream (artifact parsed before generation
  // completes), initializing from artifact?.content would seed these states
  // with partial code. The isStreaming gate then freezes to that partial value
  // and ReactViz throws a SyntaxError. Initialize to "" when streaming so the
  // frozen gate holds an empty string (safe) until the post-stream sync effect
  // sets the real final content.
  const _mountIsGenerating = isGenerating;
  const _mountIsStreamingFromStore = !!(messageId && useStreamingStateStore.getState().activeStreams[messageId]?.streaming);
  const _mountIsStreaming = _mountIsGenerating || _mountIsStreamingFromStore;
  const [editableCode, setEditableCode] = useState(() => _mountIsStreaming ? "" : (activeArtifact?.content ?? ""));
  const [savedBase, setSavedBase] = useState(() => _mountIsStreaming ? "" : (activeArtifact?.content ?? ""));
  const [appliedPreviewCode, setAppliedPreviewCode] = useState(() => _mountIsStreaming ? "" : (activeArtifact?.content ?? ""));
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [hasLintErrors, setHasLintErrors] = useState(false);
  // Full diagnostics array — { message, severity, line, col }[].
  // hasLintErrors is derived below (any error-severity entry).
  const [diagnostics, setDiagnostics] = useState([]);
  // True when the currently-rendered preview is showing an error (React useRunner
  // error or HTML iframe runtime error). Enables the Run button even when
  // editableCode === appliedPreviewCode so the user can retry / re-run after fixing.
  const [previewHasError, setPreviewHasError] = useState(false);
  // Incremented on retry-run (editableCode === appliedPreviewCode) to force
  // ReactViz to remount and re-execute the same code string.
  const [reactVizRunKey, setReactVizRunKey] = useState(0);
  // Incremented on retry-run for HTML artifacts to force a fresh iframe load.
  const [iframePreviewKey, setIframePreviewKey] = useState(0);
  const [editorWidth, setEditorWidth] = useState(380);
  const [previewWidth, setPreviewWidth] = useState(null); // null = fluid, 1280/768/390 = breakpoint
  // Inline AI editor state
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  // True when the space owner is running an inline AI edit on this artifact
  // and this viewer is a collaborator (not the one doing the edit).
  const isRemoteEditing = useArtifactEditSignal((state) => !!state.editingAnswers[answerId]);
  // Phase 2 — streaming visibility for inline AI edit:
  //   editPhase: "analyzing" (request in flight, no tokens yet)
  //            | "drafting"  (first chunk arrived, tokens streaming)
  //            | null        (idle)
  //   editStreamTail: trailing slice of the streamed buffer for the ghost preview
  const [editPhase, setEditPhase] = useState(null);
  const [editStreamTail, setEditStreamTail] = useState("");
  const [editError, setEditError] = useState(null);
  // Phase A — SEARCH/REPLACE protocol surfaces:
  //   editIntent: one-line summary the model emitted before its blocks
  //   editApplyFailure: structured failure payload when blocks could not be
  //                     applied (raw_response + failed_blocks for retry UI)
  const [editIntent, setEditIntent] = useState(null);
  const [editApplyFailure, setEditApplyFailure] = useState(null);
  const [editorModelId, setEditorModelId] = useState(null);
  const [diffPending, setDiffPending] = useState(false);
  const [showSavedDiff, setShowSavedDiff] = useState(false);
  // Phase B — per-hunk Accept/Reject decisions for the active inline AI edit.
  // Map<hunkId, "rejected" | "accepted">. Absent ⇒ pending (still applied).
  // Cleared on diff gate close (accept-all, reject-all, or new edit start).
  const [hunkDecisions, setHunkDecisions] = useState(() => new Map());
  // Version history panel + browse mode state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [browsePreviewCode, setBrowsePreviewCode] = useState(null);
  const [browsingVersion, setBrowsingVersion] = useState(null);
  // Canvas text-edit mode — true while the user is editing text in the HTML preview (P3)
  const [canvasEditMode, setCanvasEditMode] = useState(false);
  // P6 — AI popover: shown when the user clicks a non-text element in edit mode
  const [aiPopover, setAiPopover] = useState(null); // { x, y, outerHtml } | null
  // Snapshot of code before an inline AI edit — used by B1 diff gate
  const preEditSnapshotRef = useRef(null);
  const preEditDiagnosticsRef = useRef([]);
  // #14 — raw user prompt that drove the most recent inline AI edit. Captured at
  // submit time and threaded into persistSave on accept so the version doc stores
  // the user's intent (powers history-panel labels and click-to-replay).
  const lastEditPromptRef = useRef(null);
  // Captures the full AI-generated code at stream completion — used by diff memo
  // (avoids stale-closure over editableCode in the [diffPending]-only dep array)
  const postEditCodeRef = useRef(null);
  // Tracks whether the most recent unsaved change came from an accepted AI edit
  const aiEditAcceptedRef = useRef(false);
  // Tracks the version number being restored (for version_trigger on save)
  const restoreVersionRef = useRef(null);
  // Pending restore: set when user clicks Restore before content is fetched
  const pendingRestoreRef = useRef(null);
  // Canvas text-edit refs (P2/P4/P5)
  const iframeRef = useRef(null);          // HTML preview iframe
  const pidMapRef = useRef(null);          // latest tokenization map (updated each render)
  const editSourceRef = useRef(null);      // HTML source that was in preview when edit started
  const iframeClickListenerRef = useRef(null); // P6: stored handler ref for removeEventListener
  // #660 — S1: stores { text, from, to } of the current CodeMirror selection (primary range only)
  const editorSelectionRef = useRef(null);
  const [editSelection, setEditSelection] = useState(null); // { lineCount } — drives badge
  const splitContainerRef = useRef(null);
  const previewRef = useRef(null);
  const historyAnchorRef = useRef(null);
  const isDragging = useRef(false);
  // When dragging the splitter we render a transparent overlay over the
  // entire split container so the cursor never crosses into the iframe
  // (where parent-window mousemove/mouseup events are swallowed by the
  // sandboxed document, leaving isDragging stuck true).
  const [isDraggingSplit, setIsDraggingSplit] = useState(false);
  // Phase 3 — ref to the active CodeMirror EditorView (whichever artifact type
  // is mounted). Used to scroll to the first changed line after an inline AI edit.
  const cmViewRef = useRef(null);
  const handleEditorMount = useCallback((view) => { cmViewRef.current = view; }, []);
  // Phase 4 — ref to the AI prompt <input>, so Tweak can refocus it after reject.
  const editPromptInputRef = useRef(null);
  // Phase 4 — user toggle to hide diff highlights while still in the diff gate
  // (so they can read the clean accepted code before deciding).
  const [hideInlineDiff, setHideInlineDiff] = useState(false);

  // ── Version history ────────────────────────────────────────────────────────
  const {
    versions,
    pinnedVersion,
    isLoading: versionsLoading,
    loadVersion,
    versionContent,
    isLoadingVersion,
    clearVersionContent,
    pinVersion,
  } = useArtifactVersions(answerId, chatOwnerId);

  // Sync fetched version content into browse preview state. Saved versions
  // hold the full answer string — extract the inner artifact code so the viz
  // layer (ReactViz / PhysicsViz / iframe srcDoc) receives executable code,
  // not markdown prose wrapped around a fenced block.
  useEffect(() => {
    if (!versionContent) return;
    setBrowsePreviewCode(extractArtifactCode(versionContent.content, clampedItemIndex));
  }, [versionContent, clampedItemIndex]);

  // Tracks the active drag's teardown so it can be forced from the unmount
  // effect below, in addition to the normal mouseup/blur paths.
  const dragCleanupRef = useRef(null);
  const onDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    setIsDraggingSplit(true);
    const onMove = (ev) => {
      if (!isDragging.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const raw = clientX - rect.left;
      const min = 200;
      const max = rect.width - 200;
      setEditorWidth(Math.min(Math.max(raw, min), max));
    };
    const onUp = () => {
      isDragging.current = false;
      setIsDraggingSplit(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("blur", onUp);
      dragCleanupRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    // Safety: if the user releases the mouse over an iframe (which swallows
    // the parent's mouseup), the window loses focus when the iframe takes it
    // — use that as a fallback to end the drag.
    window.addEventListener("blur", onUp);
    // Safety: if the component unmounts mid-drag (e.g. the user switches
    // chats while still holding the resize handle), onUp is never called by a
    // real mouse event, so these window listeners would otherwise leak
    // forever, firing against stale refs/setters for an unmounted component.
    dragCleanupRef.current = onUp;
  }, []);
  useEffect(() => () => dragCleanupRef.current?.(), []);
  const { isFullscreen, enterFullscreen } = useNativeFullscreen(previewRef);
  // isGenerating prop from parent catches the pre-flag window (optimistic update before store is set)
  const isLoadingStore = ChatStore((state) => state.isLatestMessageLoading);
  const isLoading = isGenerating || isLoadingStore;

  // Per-message streaming gate — true while this specific message is actively
  // streaming. Used to suppress mid-stream artifact rendering: the iframe must
  // NOT receive partial code on every chunk; mount once on GENERATION_COMPLETE
  // with the final content. Falls back to `isLoading` for the brief pre-flag
  // window before the store entry is created.
  const isStreamingFromStore = useStreamingStateStore(
    (state) => !!(messageId && state.activeStreams[messageId]?.streaming)
  );
  const isStreaming = isGenerating || isStreamingFromStore;

  // Hold prior version visible during regeneration — store last canvas-typed artifact so
  // the canvas never blanks while the next version streams in. We intentionally exclude
  // `type: "text"` because parseNebulaResponse falls back to text the moment any leading
  // prose streams in (before the <nebula> block opens) — treating that as a "real" artifact
  // would overwrite the previous canvas before the new one is ready.
  const CANVAS_TYPES = useMemo(() => new Set(["html", "react", "plotly", "mermaid", "markmap", "image", "video", "audio"]), []);
  const isCanvasArtifact = !!activeArtifact && CANVAS_TYPES.has(activeArtifact.type);
  const previousArtifactRef = useRef(null);
  useEffect(() => {
    if (isCanvasArtifact) previousArtifactRef.current = activeArtifact;
  }, [activeArtifact, isCanvasArtifact]);

  // Ghost = "we're regenerating and shouldn't show partial new content". Hold
  // the prior canvas visible for the entire stream window when one exists —
  // paired with the editableCode sync gates below. Falls back to the narrower
  // condition (no canvas type yet on incoming) for non-streaming code paths.
  const isGhosted = !!previousArtifactRef.current && (isStreaming || (!isCanvasArtifact && isLoading));
  const displayArtifact = isGhosted ? previousArtifactRef.current : activeArtifact;
  const displayItems = useMemo(() => {
    if (Array.isArray(displayArtifact?.items) && displayArtifact.items.length > 0) {
      return displayArtifact.items;
    }
    return displayArtifact ? [displayArtifact] : [];
  }, [displayArtifact]);

  // Keyboard Left/Right navigation across multi-item artifacts when not editing text
  useEffect(() => {
    if (!isVisible || displayItems.length <= 1 || viewMode === "code" || canvasEditMode || isEditing) return;
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveItemIndex((i) => (i - 1 + displayItems.length) % displayItems.length);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setActiveItemIndex((i) => (i + 1) % displayItems.length);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isVisible, displayItems.length, viewMode, canvasEditMode, isEditing]);

  // Early type detection — peek at the streamed rawContent for an opening fence
  // (or storage-path marker) so we can tell what the new artifact will be before
  // it finishes parsing. Used to decide whether to keep showing the prior canvas
  // (same type → preserve context) or swap to a neutral skeleton (different type
  // → old content would be misleading).
  const incomingType = useMemo(() => {
    if (!isGhosted || !rawContent) return null;
    const t = rawContent;
    if (/```(?:html-viz|html-live|html)(?:\s|\n|$)/i.test(t)) return "html";
    if (/```(?:react-viz|jsx-live)(?:\s|\n|$)/i.test(t)) return "react";
    if (/```(?:physics-viz|json-viz)(?:\s|\n|$)/i.test(t)) return "plotly";
    if (/```mermaid(?:\s|\n|$)/i.test(t)) return "mermaid";
    if (/```markmap(?:\s|\n|$)/i.test(t)) return "markmap";
    if (/!?\[[^\]]*\]\((?!https?:)[^)\s]+\.(?:mp4|webm|mov)(?:\?[^)\s]*)?\)/i.test(t)) return "video";
    if (/!?\[[^\]]*\]\((?!https?:)[^)\s]+\.(?:mp3|wav|ogg|aac|m4a)(?:\?[^)\s]*)?\)/i.test(t)) return "audio";
    if (/!?\[[^\]]*\]\((?!https?:)[^)\s]+\.(?:png|jpg|jpeg|gif|webp)(?:\?[^)\s]*)?\)/i.test(t)) return "image";
    return null;
  }, [isGhosted, rawContent]);

  // Type-mismatch — known incoming type and it differs from the previous canvas.
  // In this case the prior content is actively misleading; render a neutral skeleton instead.
  const typeMismatch = isGhosted && !!incomingType && previousArtifactRef.current?.type !== incomingType;

  // Crossfade swap — when the ghosted state flips off (new <nebula> just parsed),
  // briefly apply a fade-in animation so the swap reads as smooth rather than abrupt.
  // Single-shot: fires only on the ghosted→real transition, not on each streamed token.
  const [justSwapped, setJustSwapped] = useState(false);
  // Change-count pill (P3) — surfaces "N changes" briefly after a same-type swap so the user
  // knows the regeneration produced edits without auto-flashing the diff view. Click → opens diff.
  const [changeBadge, setChangeBadge] = useState(null); // { count } | null
  // Report mode — shows preamble/postamble text alongside the artifact in preview.
  const [reportMode, setReportMode] = useState(false);
  // Editable copy of rawContent for the report-mode left panel (rawContent is a read-only prop).
  const [editableRaw, setEditableRaw] = useState(rawContent ?? "");
  const [savedRawBase, setSavedRawBase] = useState(rawContent ?? "");
  const [editablePreamble, setEditablePreamble] = useState(displayArtifact?.preamble ?? "");
  const [editablePostamble, setEditablePostamble] = useState(displayArtifact?.postamble ?? "");
  useEffect(() => {
    setEditableRaw(rawContent ?? "");
    setSavedRawBase(rawContent ?? "");
    setEditablePreamble(displayArtifact?.preamble ?? "");
    setEditablePostamble(displayArtifact?.postamble ?? "");
  }, [rawContent, displayArtifact?.preamble, displayArtifact?.postamble]);
  const prevGhostedRef = useRef(isGhosted);
  useEffect(() => {
    if (prevGhostedRef.current && !isGhosted) {
      setJustSwapped(true);
      const swapT = setTimeout(() => setJustSwapped(false), 260);

      // Compute change count if same-type, diffable, and content actually differs.
      const fromArtifact = previousArtifactRef.current;
      const toArtifact = activeArtifact;
      let badgeT = null;
      if (
        fromArtifact && toArtifact &&
        fromArtifact.type === toArtifact.type &&
        getCaps(toArtifact.type)?.canDiff &&
        fromArtifact.content !== toArtifact.content
      ) {
        const lines = diffArtifacts(fromArtifact.content, toArtifact.content);
        const count = lines.reduce((n, l) => n + (l.type === "added" || l.type === "removed" ? 1 : 0), 0);
        if (count > 0) {
          setChangeBadge({ count });
          badgeT = setTimeout(() => setChangeBadge(null), 5000);
        }
      }

      prevGhostedRef.current = isGhosted;
      return () => { clearTimeout(swapT); if (badgeT) clearTimeout(badgeT); };
    }
    prevGhostedRef.current = isGhosted;
  }, [isGhosted, activeArtifact]);
  const swapClass = justSwapped ? "nebula-canvas-swap" : "";

  // Report mode — available when the response contained surrounding text.
  // Declared before any early return so the useEffect runs on every render.
  const hasReportContent = !isGenerating &&
    !!displayArtifact &&
    displayArtifact.type !== "text" &&
    (displayArtifact.preamble?.length > 0 || displayArtifact.postamble?.length > 0);
  useEffect(() => { if (!hasReportContent) setReportMode(false); }, [hasReportContent]);

  const openDiffFromBadge = useCallback(() => {
    setChangeBadge(null);
    setDiffMode(true);
  }, []);

  // Title display during generation — derived from latest activityItem label when available.
  const latestActivity = activityItems[activityItems.length - 1];
  const displayTitle = latestActivity?.label || "Beaming";
  const titleVisible = true;

  // Diff — computed only when diff mode is active and both artifacts are HTML
  const diffLines = useMemo(() => {
    if (!diffMode || !previousArtifact || !activeArtifact) return [];
    if (!getCaps(activeArtifact.type).canDiff || activeArtifact.type !== previousArtifact.type) return [];
    return diffArtifacts(previousArtifact.content, activeArtifact.content);
  }, [diffMode, previousArtifact, activeArtifact]);

  // Exit diff mode and reset edit state whenever a new artifact arrives.
  // While the artifact streams (same artifact reference, content keeps growing),
  // keep `editableCode` and `savedBase` in sync with the latest content as long
  // as the user hasn't typed anything — mirroring CodeBlock's pattern. This
  // prevents the Save button from appearing spuriously during streaming.
  // Skip while streaming so the iframe (which renders `editableCode`) doesn't
  // refresh on every chunk. Effect re-runs when `isStreaming` flips false — at
  // that point `activeArtifact?.content` holds the final chunk and we sync once.
  useEffect(() => {
    if (isStreaming) return;
    setDiffMode(false);
    setHasUserEdited(false);
    setEditableCode(activeArtifact?.content ?? "");
    setSavedBase(activeArtifact?.content ?? "");
    setAppliedPreviewCode(activeArtifact?.content ?? "");
    setDiffPending(false);
    setShowSavedDiff(false);
    setShowVersionHistory(false);
    setBrowsePreviewCode(null);
    setBrowsingVersion(null);
    restoreVersionRef.current = null;
    pendingRestoreRef.current = null;
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
    setCanvasEditMode(false);
    editSourceRef.current = null;
    clearVersionContent();
    // Dep is the artifact CONTENT (and type + active tab), not the object reference. Parent
    // recomputes `artifact` on every render \u2014 if we keyed on the reference, this
    // effect would fire constantly and wipe `diffPending` / pre-/post-edit refs,
    // making the inline AI edit Accept/Reject gate disappear immediately after
    // generation completes.
  }, [activeArtifact?.content, activeArtifact?.type, clampedItemIndex, isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isStreaming) return;
    if (!hasUserEdited) {
      const next = activeArtifact?.content ?? "";
      setEditableCode(next);
      setSavedBase(next);
      setAppliedPreviewCode(next);
    }
  }, [activeArtifact?.content, clampedItemIndex, isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  // Code that actually drives the live preview renderers (ReactViz, iframe,
  // PhysicsViz). Two competing requirements:
  //   1. While streaming, hold the last completed code (appliedPreviewCode is
  //      frozen by the sync effect above) so the renderer doesn't try to
  //      parse half-written chunks and flash a syntax error.
  //   2. While NOT streaming, read activeArtifact?.content synchronously so
  //      navigating between chats doesn't show one frame of the previous
  //      chat's stale appliedPreviewCode before the post-paint effect runs.
  // When the user has edited, always prefer their applied code.
  const livePreviewCode = !isVisible
    ? "" // suspended: canvas is mounted but hidden — feed empty code so the
         // iframe / ReactViz / PhysicsViz renderers idle instead of running in
         // the background. Editor state is preserved; preview re-runs on return.
    : hasUserEdited
    ? appliedPreviewCode
    : isStreaming
      ? appliedPreviewCode
      : (activeArtifact?.content ?? appliedPreviewCode ?? "");
  const visiblePreviewCode = isVisible
    ? (browsePreviewCode ?? livePreviewCode ?? "")
    : "";

  // Warn browser if user tries to navigate away mid-edit
  useEffect(() => {
    if (!isEditing) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEditing]);

  const { downloadFile } = useDownloadFile({ caller: "NebulaCanvas" });

  // Activity overlay — fires during full regeneration (isGhosted) AND during
  // inline AI edit (isEditing). Same visual: animated gradient border around
  // the canvas. No dim, no center overlay — content stays crisp and referenceable.
  const showActivityOverlay = isGhosted || isEditing;
  const ghostedOverlay = showActivityOverlay ? (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {/* Photon — a single bright spot orbits the perimeter clockwise.
          Conic gradient anchored at the center, with a narrow bright arc and
          a softer trailing tail; rotated by an animated CSS variable so the
          hot spot travels around the border. Inherits parent border-radius
          so it tracks both rounded and square containers cleanly. */}
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{
          padding: "2px",
          background: "conic-gradient(from var(--nebula-photon-angle, 0deg), rgba(75,159,225,0) 0deg, rgba(75,159,225,0) 240deg, rgba(75,159,225,0.35) 300deg, rgba(124,77,255,0.85) 358deg, rgba(75,159,225,0.35) 60deg, rgba(75,159,225,0) 120deg, rgba(75,159,225,0) 360deg)",
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          animation: "nebula-photon-orbit 6s cubic-bezier(0.45, 0, 0.55, 1) infinite",
        }}
      />
    </div>
  ) : null;

  // Inline status pill — rendered in the header row, not absolutely positioned,
  // so it never overlaps the icon buttons on the right side.
  // Shown for full regeneration (isGhosted) and inline AI edit (isEditing).
  // Phase 2 — when editing, label cycles with editPhase.
  const editPhaseLabel = editPhase === "analyzing"
    ? "Analyzing your edit"
    : editPhase === "drafting"
      ? "Drafting changes"
      : "Editing with AI";
  const pillLabel = isGhosted ? displayTitle : (isEditing ? editPhaseLabel : null);
  const regenStatusPill = pillLabel ? (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fill-canvas-accent/10 dark:bg-fill-canvas-accent/15 border border-edge-canvas-accent/25">
      <span className="w-1.5 h-1.5 rounded-full bg-fill-canvas-accent animate-pulse flex-shrink-0" />
      <span
        className="text-xs text-ink-canvas-accent italic font-medium whitespace-nowrap"
        style={{ opacity: titleVisible ? 1 : 0, transition: "opacity 0.25s ease" }}
      >
        {pillLabel}
        <span className="beaming-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </span>
    </div>
  ) : null;

  // Change-count badge — appears briefly after a same-type swap to surface
  // "what changed" without forcing diff view. Click → open diff. Auto-dismiss 5s.
  const changeBadgeOverlay = changeBadge ? (
    <button
      type="button"
      onClick={openDiffFromBadge}
      className="absolute top-2 right-2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fill-white/95 dark:bg-fill-page-surface-dark/95 backdrop-blur-sm border border-edge-canvas-accent/40 shadow-md hover:shadow-lg hover:border-edge-canvas-accent transition-all nebula-badge-in"
      title="View what changed"
    >
      <span className="text-xs">✨</span>
      <span className="text-xs font-medium text-ink-canvas-accent dark:text-ink-canvas-accent whitespace-nowrap">
        {changeBadge.count} {changeBadge.count === 1 ? "change" : "changes"}
      </span>
    </button>
  ) : null;

  const handleDownload = async () => {
    if (!displayArtifact) return;
    if (displayArtifact.type === "html") {
      downloadFile(prepareHtml(editableCode), `nebula-${Date.now()}.html`, "text/html");
    } else if (displayArtifact.type === "react") {
      downloadFile(editableCode, `nebula-${Date.now()}.jsx`, "text/javascript");
    } else if (displayArtifact.type === "plotly") {
      if (!previewRef.current) return;
      toPng(previewRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true })
        .then((dataUrl) => {
          const a = document.createElement("a");
          a.href = dataUrl;
          a.download = `nebula-${Date.now()}.png`;
          a.click();
        });
    } else if (displayArtifact.type === "mermaid") {
      const svgEl = previewRef.current?.querySelector('svg');
      if (svgEl) {
        const svgStr = new XMLSerializer().serializeToString(svgEl);
        downloadFile(svgStr, `nebula-${Date.now()}.svg`, "image/svg+xml");
      }
    } else if (displayArtifact.type === "markmap") {
      if (!previewRef.current) return;
      toPng(previewRef.current, { cacheBust: true, pixelRatio: 2, skipFonts: true })
        .then((dataUrl) => {
          const a = document.createElement("a");
          a.href = dataUrl;
          a.download = `nebula-${Date.now()}.png`;
          a.click();
        });
    } else if (["image", "video", "audio"].includes(displayArtifact.type)) {
      // displayArtifact.content may be a canonical asset_id, a raw GCS storage
      // path, or a direct URL.
      const extMap = { image: "png", video: "mp4", audio: "mp3" };
      const ext = extMap[displayArtifact.type] ?? "bin";
      const filename = `nebula-${Date.now()}.${ext}`;
      try {
        const orgId = UserAuthStore.getState().orgId;
        const _isAlreadyUrl = displayArtifact.content.startsWith('/storage/') || displayArtifact.content.startsWith('http');
        const _isAsset = displayArtifact.content.startsWith('asset_');
        let proxyUrl;
        if (_isAlreadyUrl) {
          proxyUrl = displayArtifact.content;
        } else if (_isAsset) {
          const resolved = useResolvedMediaUrlStore.getState().urls[displayArtifact.content];
          proxyUrl = resolved || `/storage/assets/${encodeURIComponent(displayArtifact.content)}/resolve`;
        } else {
          proxyUrl = `/storage/download-media?file_path=${encodeURIComponent(displayArtifact.content)}&org_id=${encodeURIComponent(orgId)}`;
        }
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        // Fallback: open the proxy URL directly in the browser tab
        const orgId = UserAuthStore.getState().orgId;
        const _isAlreadyUrl = displayArtifact.content.startsWith('/storage/') || displayArtifact.content.startsWith('http');
        const fallbackUrl = _isAlreadyUrl
          ? displayArtifact.content
          : `/storage/download-media?file_path=${encodeURIComponent(displayArtifact.content)}&org_id=${encodeURIComponent(orgId)}`;
        window.open(fallbackUrl, "_blank");
      }
    }
  };

  // Build the updated full answer string for save — detects the original
  // fence tag and uses `replaceCodeBlockContent` so we never accidentally
  // overwrite a different code block earlier in the answer.
  const buildUpdatedContent = useCallback(
    (code) => {
      if (!activeArtifact) return null;
      // Text artifacts are plain content — no fence to reconstruct around them.
      if (activeArtifact.type === "text") return code;
      if (!rawContent) return null;
      return buildUpdatedNebulaContent(rawContent, activeArtifact.type, code, activeArtifact.typeOccurrence ?? 0);
    },
    [rawContent, activeArtifact]
  );
  const { saveStatus, save: persistSave } = useArtifactSave({ answerId, chatOwnerId, buildUpdatedContent });

  const { chat_id } = useSparkContext();

  const { startEdit, cancel: cancelEdit } = useInlineEdit({
    onChunk: (buf) => {
      // Phase 2 — first chunk transitions phase to "drafting" and starts the
      // streaming ghost preview (last 160 chars of the buffer).
      // Phase A note: for non-selection SEARCH/REPLACE edits this fires exactly
      // once with the full merged file. Skip the streaming-tail preview in
      // that case — the intent line already conveys what the AI did, and a
      // 160-char tail of the merged file is noise.
      setEditPhase("drafting");
      if (editorSelectionRef.current) {
        setEditStreamTail(buf.length > 160 ? buf.slice(-160) : buf);
        const { from, to } = editorSelectionRef.current;
        const base = preEditSnapshotRef.current ?? "";
        setEditableCode(base.slice(0, from) + buf + base.slice(to));
      } else {
        setEditableCode(buf);
      }
    },
    onIntent: (text) => {
      // Phase A — show the model's one-line plan as soon as it arrives, while
      // the rest of the SEARCH/REPLACE response is still streaming server-side.
      setEditIntent(text);
    },
    onApplyFailed: (payload) => {
      // Phase A — soft failure (model responded but blocks couldn't be located).
      // Stash the structured payload so the inline error panel can render it
      // with a "retry" affordance. We do NOT touch editableCode — the user's
      // pre-edit code remains intact.
      setEditApplyFailure(payload);
      setEditPhase(null);
      setEditStreamTail("");
      setIsEditing(false);
    },
    onComplete: (full) => {
      // S6 — finalize splice with completed fragment
      let finalCode = full;
      if (editorSelectionRef.current) {
        const { from, to } = editorSelectionRef.current;
        const base = preEditSnapshotRef.current ?? "";
        finalCode = base.slice(0, from) + full + base.slice(to);
      }
      postEditCodeRef.current = finalCode;
      setEditableCode(finalCode);
      setHasUserEdited(true);
      setIsEditing(false);
      setEditPhase(null);
      setEditStreamTail("");
      setEditPrompt("");
      setEditSelection(null);
      editorSelectionRef.current = null;
      clearAttachments();
      setEditError(null);
      setEditApplyFailure(null);
      setHunkDecisions(new Map()); // Phase B — fresh slate per new edit
      setDiffPending(true); // B2 — open diff gate
    },
    onError: (msg) => {
      setEditError(msg);
      setIsEditing(false);
      setEditPhase(null);
      setEditStreamTail("");
    },
  });

  const {
    files: editAttachments,
    fileInputRef: editFileInputRef,
    openFilePicker: openEditFilePicker,
    onFileInputChange: onEditFileInputChange,
    handlePaste: onEditPaste,
    removeFile: removeEditFile,
    clearFiles: clearAttachments,
    assetIds: editAssetIds,
  } = useInlineEditAttachments(chat_id);

  // Cancelling aborts the fetch in the hook, but the hook intentionally does
  // NOT fire onError for AbortError — so we must reset the canvas-local edit
  // state here, otherwise `isEditing` stays true and the stop button / input
  // appear stuck. Used by both the stop button and the Esc shortcut.
  const handleCancelEdit = useCallback(() => {
    cancelEdit?.();
    setIsEditing(false);
    setEditPhase(null);
    setEditStreamTail("");
    setEditIntent(null);
    setEditApplyFailure(null);
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
    lastEditPromptRef.current = null;
  }, [cancelEdit]);

  // Presentation skill — slide click pre-populates the edit prompt
  useEffect(() => {
    if (displayArtifact?.type !== "react") return;
    const handler = (e) => {
      const { slide_index, slide_title } = e.detail;
      setEditPrompt((prev) => prev.trim() ? prev : `Edit slide ${slide_index} ("${slide_title}"): `);
      setTimeout(() => editPromptInputRef.current?.focus(), 0);
    };
    window.addEventListener("NEBULA_SLIDE_FOCUSED", handler);
    return () => window.removeEventListener("NEBULA_SLIDE_FOCUSED", handler);
  }, [displayArtifact?.type]);

  // Esc cancels an in-flight inline AI edit
  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCancelEdit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEditing, handleCancelEdit]);

  const handleEditSubmit = useCallback(() => {
    if ((!editPrompt.trim() && editAttachments.length === 0) || isEditing || !displayArtifact) return;
    preEditSnapshotRef.current = editableCode;
    preEditDiagnosticsRef.current = diagnostics; // B5 — snapshot lint errors before edit
    lastEditPromptRef.current = editPrompt; // #14 — capture intent for the version doc
    setEditError(null);
    setEditApplyFailure(null);
    setEditIntent(null);
    setIsEditing(true);
    setEditPhase("analyzing"); // Phase 2 — phase flips to "drafting" on first chunk
    setEditStreamTail("");
    // S3 — scope to selection when present
    const sel = editorSelectionRef.current;
    startEdit({
      prompt: editPrompt,
      currentCode: sel ? sel.text : editableCode,
      language: displayArtifact.type,
      diagnostics,
      answerId,
      chatId: chat_id,
      modelId: editorModelId,
      assetIds: editAssetIds,
      ...(sel && { selectionStart: sel.from, selectionEnd: sel.to }),
    });
  }, [editPrompt, editAttachments, isEditing, editableCode, displayArtifact, diagnostics, answerId, chat_id, editorModelId, startEdit, editAssetIds]);

  // Phase 5 #16/#17 — submit a prompt synchronously without waiting for the
  // editPrompt state round-trip. Used by quick-action chips and "Fix errors".
  const submitPromptNow = useCallback((promptText) => {
    if (!promptText?.trim() || isEditing || !displayArtifact) return;
    setEditPrompt(promptText);
    preEditSnapshotRef.current = editableCode;
    preEditDiagnosticsRef.current = diagnostics;
    lastEditPromptRef.current = promptText; // #14 — capture intent for the version doc
    setEditError(null);
    setEditApplyFailure(null);
    setEditIntent(null);
    setIsEditing(true);
    setEditPhase("analyzing");
    setEditStreamTail("");
    const sel = editorSelectionRef.current;
    startEdit({
      prompt: promptText,
      currentCode: sel ? sel.text : editableCode,
      language: displayArtifact.type,
      diagnostics,
      answerId,
      chatId: chat_id,
      modelId: editorModelId,
      assetIds: editAssetIds,
      ...(sel && { selectionStart: sel.from, selectionEnd: sel.to }),
    });
  }, [isEditing, editableCode, displayArtifact, diagnostics, answerId, chat_id, editorModelId, startEdit, editAssetIds]);

  // Phase 5 #16 — quick-action chips per artifact type (eliminates blank-page paralysis).
  const quickActions = useMemo(() => {
    const type = displayArtifact?.type;
    if (type === "html" || type === "react") {
      return ["Make this responsive", "Add accessibility", "Refactor"];
    }
    if (type === "plotly") {
      return ["Improve labels & legend", "Add hover tooltips", "Switch chart type"];
    }
    if (type === "mermaid" || type === "markmap") {
      return ["Simplify", "Add styling", "Reorganize"];
    }
    return ["Refactor", "Add comments", "Simplify"];
  }, [displayArtifact?.type]);

  // Phase 5 #17 — build a fix-prompt from current lint diagnostics.
  const fixErrorsPrompt = useMemo(() => {
    const errs = diagnostics.filter(d => d.severity === "error").slice(0, 10);
    if (errs.length === 0) return null;
    const lines = errs.map(d => `  • line ${d.line ?? "?"}: ${d.message}`).join("\n");
    return `Fix the following ${errs.length === 1 ? "error" : "errors"} without changing unrelated behavior:\n${lines}`;
  }, [diagnostics]);

  const handleSave = async () => {
    const trigger = restoreVersionRef.current != null
      ? `restored_from_v${restoreVersionRef.current}`
      : aiEditAcceptedRef.current ? "inline_ai_edit" : "manual_edit";
    const persisted = await persistSave(editableCode, trigger);
    if (persisted != null) {
      setSavedBase(editableCode);
      setSavedRawBase(editableRaw);
      setHasUserEdited(false);
      aiEditAcceptedRef.current = false;
      restoreVersionRef.current = null;
    }
  };

  // S1 — capture primary selection from CodeMirror on every editor update
  const onLintUpdate = useCallback((update) => {
    const next = extractDiagnostics(update.state);
    setDiagnostics(next);
    setHasLintErrors(next.some(d => d.severity === "error"));
  }, []);

  const onSelectionUpdate = useCallback((viewUpdate) => {
    onLintUpdate(viewUpdate); // preserve existing lint diagnostics
    const sel = viewUpdate.state.selection.main;
    if (sel.empty) {
      editorSelectionRef.current = null;
      setEditSelection(null);
    } else {
      const text = viewUpdate.state.sliceDoc(sel.from, sel.to);
      editorSelectionRef.current = { text, from: sel.from, to: sel.to };
      // Phase 5 #15 — capture human-readable line range so the badge / placeholder
      // can read "Editing lines 42–58" instead of just "N lines selected".
      const startLine = viewUpdate.state.doc.lineAt(sel.from).number;
      const endLine = viewUpdate.state.doc.lineAt(sel.to).number;
      setEditSelection({ lineCount: endLine - startLine + 1, startLine, endLine });
    }
  }, [onLintUpdate]);
  const handleBrowseVersion = useCallback((versionNumber) => {
    setBrowsingVersion(versionNumber);
    loadVersion(versionNumber);
  }, [loadVersion]);

  const handleExitBrowse = useCallback(() => {
    setBrowsingVersion(null);
    setBrowsePreviewCode(null);
    clearVersionContent();
    pendingRestoreRef.current = null;
  }, [clearVersionContent]);

  // Commit a restore: sync editor + preview to the restored content and
  // immediately persist as a new version (trigger "restored_from_v{N}").
  // Used by both handleRestoreVersion (cached) and the post-fetch effect.
  //
  // `rawContent` is the full saved answer string. We extract the inner artifact
  // code so the editor / viz layer get executable code (not markdown prose),
  // and pass that same inner code to persistSave — buildUpdatedNebulaContent
  // rebuilds the surrounding answer from the current rawContent's fence.
  const commitRestore = useCallback(async (versionNumber, rawContent) => {
    if (rawContent == null) return;
    const code = extractArtifactCode(rawContent, clampedItemIndex);
    setEditableCode(code);
    setAppliedPreviewCode(code);
    aiEditAcceptedRef.current = false;
    restoreVersionRef.current = versionNumber;
    setBrowsingVersion(null);
    setBrowsePreviewCode(null);
    clearVersionContent();
    setShowVersionHistory(false);
    pendingRestoreRef.current = null;
    const persisted = await persistSave(code, `restored_from_v${versionNumber}`);
    if (persisted != null) {
      setSavedBase(code);
      setHasUserEdited(false);
      restoreVersionRef.current = null;
    } else {
      // Persist failed — leave content in editor and mark dirty so the user
      // can retry via the Save button (visible in code view).
      setHasUserEdited(true);
    }
  }, [clearVersionContent, persistSave, clampedItemIndex]);

  const handleRestoreVersion = useCallback((versionNumber) => {
    if (versionContent?.version_number === versionNumber) {
      // Content already fetched — commit immediately
      commitRestore(versionNumber, versionContent.content);
    } else {
      // Need to fetch first; the post-fetch effect below will commit
      pendingRestoreRef.current = versionNumber;
      loadVersion(versionNumber);
    }
  }, [versionContent, commitRestore, loadVersion]);

  // Complete a pending restore once the version content arrives.
  useEffect(() => {
    if (!versionContent) return;
    if (pendingRestoreRef.current === versionContent.version_number) {
      commitRestore(versionContent.version_number, versionContent.content);
    }
  }, [versionContent, commitRestore]);

  // ── B1–B5: Inline AI edit diff gate ────────────────────────────────────

  const handleAcceptEdit = useCallback(async () => {
    // Phase B — `editableCode` already reflects per-hunk decisions (rejected
    // hunks are absent), so persist exactly what the user is looking at.
    const accepted = editableCode;
    const promptUsed = lastEditPromptRef.current; // #14 — intent for the version doc
    setDiffPending(false);
    setHideInlineDiff(false);
    setHunkDecisions(new Map());
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
    lastEditPromptRef.current = null;
    // Persist accepted code and create a version with trigger "inline_ai_edit"
    const persisted = await persistSave(accepted, "inline_ai_edit", promptUsed);
    if (persisted != null) {
      setSavedBase(accepted);
      setHasUserEdited(false);
      aiEditAcceptedRef.current = false;
    } else {
      // Save failed — leave aiEditAcceptedRef set so manual save retries with correct trigger
      aiEditAcceptedRef.current = true;
    }
  }, [editableCode, persistSave]);

  const handleRejectEdit = useCallback(() => {
    const restored = preEditSnapshotRef.current;
    if (restored !== null) {
      setEditableCode(restored);
      setHasUserEdited(restored !== savedBase);
    }
    setDiffPending(false);
    setHideInlineDiff(false);
    setHunkDecisions(new Map());
    aiEditAcceptedRef.current = false;
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
    lastEditPromptRef.current = null;
  }, [savedBase]);

  // Phase 4 — "Tweak": revert the edit AND seed the prompt with a refinement
  // prefix so the user can iterate on the same intent without retyping context.
  const handleTweakEdit = useCallback(() => {
    const lastPrompt = editPrompt; // already cleared on success; will be empty unless user re-typed
    handleRejectEdit();
    setEditPrompt(lastPrompt ? `Refine: ${lastPrompt}` : "Refine: ");
    // Defer focus until after the input re-renders (diffPending toggles row 2).
    setTimeout(() => {
      editPromptInputRef.current?.focus();
      const el = editPromptInputRef.current;
      if (el && el.setSelectionRange) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 0);
  }, [handleRejectEdit, editPrompt]);

  // Phase 4 — keyboard shortcuts in the diff gate:
  //   Cmd/Ctrl+Enter → accept   |   Cmd/Ctrl+Z → reject (revert as one step)
  useEffect(() => {
    if (!diffPending) return;
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key === "Enter") {
        e.preventDefault();
        handleAcceptEdit();
      } else if (e.key === "z" || e.key === "Z") {
        e.preventDefault();
        handleRejectEdit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [diffPending, handleAcceptEdit, handleRejectEdit]);

  const inlineEditDiffLines = useMemo(() => {
    if (!diffPending || preEditSnapshotRef.current === null || postEditCodeRef.current === null) return [];
    return diffArtifacts(preEditSnapshotRef.current, postEditCodeRef.current);
  }, [diffPending]);

  // Phase B — group raw diff lines into discrete hunks the user can accept or
  // reject individually. `hunks` is computed once per edit and cached; the
  // user's per-hunk decisions live in `hunkDecisions` and drive the displayed
  // code + which hunks still show controls.
  const inlineEditHunks = useMemo(() => {
    if (!inlineEditDiffLines.length) return [];
    return groupIntoHunks(inlineEditDiffLines);
  }, [inlineEditDiffLines]);

  // Phase B — apply current decisions to derive the code that should be in the
  // editor RIGHT NOW. When the diff gate is closed (no diffPending) we don't
  // override editableCode — user edits flow normally.
  useEffect(() => {
    if (!diffPending || inlineEditHunks.length === 0) return;
    const pre = preEditSnapshotRef.current;
    const post = postEditCodeRef.current;
    if (pre == null || post == null) return;
    const next = buildDisplayedCode(pre, post, inlineEditHunks, hunkDecisions);
    setEditableCode(next);
  }, [diffPending, inlineEditHunks, hunkDecisions]);

  // Phase B — auto-apply the in-review code to the live preview so the user
  // can SEE the AI's change immediately (and as they toggle per-hunk
  // accept/reject) without having to click Run. Only runs while the diff gate
  // is open and there are no lint errors (don't shove broken code into the
  // iframe). Once the user accepts or rejects all, normal Run-button flow
  // takes over again.
  useEffect(() => {
    if (!diffPending) return;
    if (hasLintErrors) return;
    if (editableCode === appliedPreviewCode) return;
    setAppliedPreviewCode(editableCode);
  }, [diffPending, editableCode, hasLintErrors, appliedPreviewCode]);

  // Phase 3 — change-count summary for inline AI edit. +N added / -M removed.
  const inlineEditChangeCounts = useMemo(() => {
    if (!inlineEditDiffLines.length) return null;
    let added = 0, removed = 0;
    for (const e of inlineEditDiffLines) {
      if (e.type === "added") added += 1;
      else if (e.type === "removed") removed += 1;
    }
    return added || removed ? { added, removed } : null;
  }, [inlineEditDiffLines]);

  // Phase B — count of hunks still pending review (not rejected, not accepted).
  // Drives the header badge ("3 changes") and next/prev navigation availability.
  const pendingHunkCount = useMemo(() => {
    if (inlineEditHunks.length === 0) return 0;
    return inlineEditHunks.filter(h => hunkDecisions.get(h.id) !== "rejected" && hunkDecisions.get(h.id) !== "accepted").length;
  }, [inlineEditHunks, hunkDecisions]);

  // Phase 3 — auto-scroll editor to first changed line when an inline AI edit
  // completes (diffPending flips on with a non-empty diff). Centers the line so
  // the user immediately sees the change without hunting.
  useEffect(() => {
    if (!diffPending || !inlineEditDiffLines.length) return;
    const view = cmViewRef.current;
    if (!view) return;
    const { addedLineNos } = getDiffLineMap(inlineEditDiffLines);
    const firstLine = addedLineNos[0];
    if (!firstLine) return;
    try {
      const pos = view.state.doc.line(Math.min(firstLine, view.state.doc.lines)).from;
      view.dispatch({ effects: EditorView.scrollIntoView(pos, { y: "center" }) });
    } catch (_) { /* doc shape changed under us — ignore */ }
  }, [diffPending, inlineEditDiffLines]);

  // C1 — savedBase diff: shows unsaved changes when user toggles the GitDiff button
  const savedDiffLines = useMemo(() => {
    if (!showSavedDiff || editableCode === savedBase) return [];
    return diffArtifacts(savedBase, editableCode);
  }, [showSavedDiff, savedBase, editableCode]);

  // VS Code-style inline diff: highlight added lines directly in CodeMirror
  const _showDiffView = (diffPending && !hideInlineDiff) || showSavedDiff;
  const _activeDiffLines = diffPending ? inlineEditDiffLines : savedDiffLines;

  // Phase B — per-hunk Accept / Reject callbacks. Defined as stable refs so
  // they don't churn the diff extension on every render.
  const acceptHunk = useCallback((id) => {
    setHunkDecisions(prev => {
      const next = new Map(prev);
      next.set(id, "accepted");
      return next;
    });
  }, []);
  const rejectHunk = useCallback((id) => {
    setHunkDecisions(prev => {
      const next = new Map(prev);
      next.set(id, "rejected");
      return next;
    });
  }, []);

  // Phase B — derive hunk anchors against the CURRENT displayed code so the
  // CodeMirror extension can position controls + ghosts correctly even after
  // some hunks have been rejected (which shifts subsequent line numbers).
  const _hunkDisplay = useMemo(() => {
    if (diffPending && inlineEditHunks.length > 0) {
      return getHunkDisplayMap(editableCode, inlineEditHunks, hunkDecisions);
    }
    if (showSavedDiff && _activeDiffLines.length > 0) {
      const m = getDiffLineMap(_activeDiffLines);
      return { ...m, hunkAnchors: [] };
    }
    return null;
  }, [diffPending, showSavedDiff, inlineEditHunks, hunkDecisions, editableCode, _activeDiffLines]);

  const diffExt = useMemo(() => {
    if (!_showDiffView || !_hunkDisplay) return [];
    const { addedLineNos, removedLineNos, ghostsByLineNo, hunkAnchors } = _hunkDisplay;
    if (!addedLineNos.length && !ghostsByLineNo.size) return [];
    // Only show per-hunk controls for active inline edits, not for saved diffs
    const hunkOpts = (diffPending && hunkAnchors && hunkAnchors.length > 0)
      ? {
          anchors: hunkAnchors,
          totalHunks: inlineEditHunks.length,
          onAccept: acceptHunk,
          onReject: rejectHunk,
        }
      : null;
    return buildDiffExtension(addedLineNos, removedLineNos, ghostsByLineNo, hunkOpts);
  }, [_showDiffView, _hunkDisplay, diffPending, inlineEditHunks.length, acceptHunk, rejectHunk]);

  // Phase B — jump-to-hunk navigation. `]` next, `[` prev (when diff gate
  // is open and the user isn't typing in an input). Scrolls ONLY the editor's
  // own scroller (not the page) by setting scrollDOM.scrollTop directly —
  // EditorView.scrollIntoView can bubble up through ancestor scroll containers
  // which causes the whole canvas/page to jump.
  const jumpToHunk = useCallback((direction) => {
    const view = cmViewRef.current;
    if (!view || !_hunkDisplay) return;
    const anchors = _hunkDisplay.hunkAnchors || [];
    if (anchors.length === 0) return;
    const scrollDOM = view.scrollDOM;
    // Find current scroll position's center line, then pick next/prev anchor.
    let centerLine;
    try {
      const center = view.lineBlockAtHeight(scrollDOM.scrollTop + scrollDOM.clientHeight / 2);
      centerLine = view.state.doc.lineAt(center.from).number;
    } catch (_) {
      centerLine = 1;
    }
    let target;
    if (direction > 0) {
      target = anchors.find(a => a.displayLine > centerLine) || anchors[0];
    } else {
      const before = anchors.filter(a => a.displayLine < centerLine);
      target = before[before.length - 1] || anchors[anchors.length - 1];
    }
    try {
      const lineNo = Math.min(target.displayLine, view.state.doc.lines);
      const pos = view.state.doc.line(lineNo).from;
      const block = view.lineBlockAt(pos);
      // Center the target line within the editor's own scroll container.
      const desired = block.top - scrollDOM.clientHeight / 2 + block.height / 2;
      const max = scrollDOM.scrollHeight - scrollDOM.clientHeight;
      scrollDOM.scrollTop = Math.max(0, Math.min(max, desired));
    } catch (_) { /* ignore */ }
  }, [_hunkDisplay]);

  useEffect(() => {
    if (!diffPending || pendingHunkCount === 0) return;
    const onKey = (e) => {
      // Don't hijack while user is typing in an input/textarea
      const t = e.target;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      if (e.key === "]") { e.preventDefault(); jumpToHunk(1); }
      else if (e.key === "[") { e.preventDefault(); jumpToHunk(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [diffPending, pendingHunkCount, jumpToHunk]);

  // P2 — tokenize the HTML currently displayed in the preview iframe so text
  // elements get data-pid attributes and we have source-position metadata.
  const tokenizedForPreview = useMemo(() => {
    const src = visiblePreviewCode;
    if (!src || displayArtifact?.type !== 'html') return { taggedHtml: src, pidMap: {} };
    return tokenizeHtmlForEditing(src);
  }, [displayArtifact?.type, visiblePreviewCode]);
  // Keep pidMapRef in sync (synchronous ref assignment in render is safe in React)
  pidMapRef.current = tokenizedForPreview.pidMap;

  useEffect(() => {
    if (editableCode === savedBase) setShowSavedDiff(false);
  }, [editableCode, savedBase]);

  // ── P3-P5: Canvas text editing ──────────────────────────────────────────────
  // P4 — inject contenteditable on all [data-pid] elements inside the iframe
  const injectEditMode = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    doc.querySelectorAll('[data-pid]').forEach((el) => {
      el.contentEditable = 'plaintext-only';
      el.style.outline = `2px dashed ${getColorSchemePalette(colorScheme, theme).primary}`;
      el.style.borderRadius = '2px';
      el.style.cursor = 'text';
      el.style.minWidth = '1em';
    });
    // P6 — click listener: shows AI-edit popover when user clicks a non-text element
    if (iframeClickListenerRef.current) {
      doc.removeEventListener('click', iframeClickListenerRef.current);
    }
    const clickHandler = (e) => {
      // Text-bearing elements handle their own editing via contenteditable
      if (e.target.closest('[data-pid]')) return;
      const iframeRect = iframeRef.current?.getBoundingClientRect();
      if (!iframeRect) return;
      const x = iframeRect.left + e.clientX;
      const y = iframeRect.top + e.clientY;
      const raw = (e.target.outerHTML ?? '').trim();
      const outerHtml = raw.length > 500 ? raw.slice(0, 500) + '\u2026' : raw;
      setAiPopover({ x, y, outerHtml });
    };
    iframeClickListenerRef.current = clickHandler;
    doc.addEventListener('click', clickHandler);
  }, [colorScheme, theme]);

  const enterCanvasEditMode = useCallback(() => {
    editSourceRef.current = browsePreviewCode ?? appliedPreviewCode;
    setCanvasEditMode(true);
  }, [browsePreviewCode, appliedPreviewCode]);

  // P5 — on exit: read changed text from iframe DOM, patch editableCode + appliedPreviewCode
  const exitCanvasEditMode = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc) {
      // P6 — remove click listener before exiting
      if (iframeClickListenerRef.current) {
        doc.removeEventListener('click', iframeClickListenerRef.current);
        iframeClickListenerRef.current = null;
      }
      const changedPids = {};
      doc.querySelectorAll('[data-pid]').forEach((el) => {
        const pid = Number(el.getAttribute('data-pid'));
        const mapEntry = pidMapRef.current?.[pid];
        if (mapEntry) {
          const currentText = el.innerText.trim();
          if (currentText !== mapEntry.originalText) {
            changedPids[pid] = currentText;
          }
        }
        // Always undo the editing affordances we injected, even if no
        // mapEntry was found — otherwise the iframe DOM stays editable
        // after the React button shows "off" (Bug: visually disabled but
        // still editable).
        el.removeAttribute('contenteditable');
        el.style.outline = '';
        el.style.borderRadius = '';
        el.style.cursor = '';
        el.style.minWidth = '';
      });
      if (Object.keys(changedPids).length > 0 && editSourceRef.current !== null) {
        const patched = buildPatchedHtml(
          editSourceRef.current,
          pidMapRef.current ?? {},
          changedPids,
        );
        setEditableCode(patched);
        setAppliedPreviewCode(patched);
        setHasUserEdited(true);
      }
    }
    editSourceRef.current = null;
    setCanvasEditMode(false);
    setAiPopover(null);
  }, []);

  // P6 — accept: pre-fill the inline AI prompt with element context, switch to code view
  const handleAiPopoverAccept = useCallback(() => {
    if (!aiPopover) return;
    setEditPrompt(aiPopover.outerHtml);
    exitCanvasEditMode();
    setViewMode('code');
  }, [aiPopover, exitCanvasEditMode]);

  const handleIframeLoad = useCallback(() => {
    if (canvasEditMode) injectEditMode();
    // Reset error state on each fresh load, then listen for runtime errors so the
    // Run button can be re-enabled when the preview is broken.
    setPreviewHasError(false);
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.onerror = () => setPreviewHasError(true);
      win.addEventListener('unhandledrejection', () => setPreviewHasError(true));
    }
  }, [canvasEditMode, injectEditMode]);

  // Re-inject whenever canvas edit mode is toggled on (iframe may already be loaded)
  useEffect(() => {
    if (canvasEditMode) injectEditMode();
  }, [canvasEditMode, injectEditMode]);

  // ── Type-mismatch skeleton ──────────────────────────────────────────────────
  // The new artifact will be a different canvas type than the previous one
  // (e.g. chart → video). Rendering the prior canvas would be misleading, so
  // show a neutral skeleton sized to the canvas area, with the status pill on top.
  if (typeMismatch) {
    return (
      <div className="relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden rounded-xl bg-fill-gray-100 dark:bg-fill-hex-15161a">
        {/* Skeleton shimmer fill */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(90deg, transparent 0%, rgba(75,159,225,0.10) 40%, rgba(124,77,255,0.14) 50%, rgba(75,159,225,0.10) 60%, transparent 100%)",
            backgroundSize: "200% 100%",
            animation: "nebula-shimmer 1.6s ease-in-out infinite",
          }}
        />
        {ghostedOverlay}{changeBadgeOverlay}
      </div>
    );
  }

  // ── Empty / loading state ───────────────────────────────────────────────────
  if (!displayArtifact) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 min-h-0">
        <div
          className={`text-4xl select-none transition-all ${isLoading ? "animate-pulse" : ""}`}
        >
          ◈
        </div>
        {isLoading ? (
          <p
            className="text-sm text-ink-canvas-accent dark:text-ink-canvas-accent text-center max-w-xs leading-relaxed italic font-medium"
            style={{
              opacity: titleVisible ? 1 : 0,
              transition: "opacity 0.25s ease",
              minHeight: "1.5em",
            }}
          >
            {displayTitle}
            <span className="beaming-dots">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </p>
        ) : (
          <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 text-center max-w-xs leading-relaxed">
            Describe what you want to create.<br />
            A page, an image, a report, a video.
          </p>
        )}
      </div>
    );
  }

  // Capabilities for the current artifact type.
  const caps = getCaps(displayArtifact.type);
  const isCodeDirty = hasUserEdited && editableCode !== savedBase;
  const isRawDirty  = reportMode && editableRaw !== savedRawBase;
  const isDirty     = isCodeDirty || isRawDirty;

  const onEditorChange = (val) => {
    setEditableCode(val);
    if (!hasUserEdited) setHasUserEdited(true);
    aiEditAcceptedRef.current = false;
  };
  // In report mode the left panel edits editableRaw. We also extract the chart
  // code so isCodeDirty / Run / Save / Reset all respond correctly.
  const onRawChange = (val) => {
    setEditableRaw(val);
    const parsed = parseNebulaResponse(val);
    if (parsed) {
      const target = parsed.items?.[clampedItemIndex] ?? parsed;
      onEditorChange(target.content);
      setEditablePreamble(target.preamble ?? "");
      setEditablePostamble(target.postamble ?? "");
    }
  };
  const resetEdits = () => {
    setEditableCode(savedBase);
    setHasUserEdited(false);
    setDiagnostics([]);
    setHasLintErrors(false);
    setDiffPending(false);
    setShowSavedDiff(false);
    setEditableRaw(savedRawBase);
    // savedRawBase intentionally not reset here — reset goes back to last save,
    // matching the same contract as setEditableCode(savedBase) above.
    // Re-derive preamble/postamble from savedRawBase so both panels land at
    // the same last-save point (not the original prop which may be further back).
    const savedParsed = parseNebulaResponse(savedRawBase);
    const targetSaved = savedParsed?.items?.[clampedItemIndex] ?? savedParsed;
    setEditablePreamble(targetSaved?.preamble ?? "");
    setEditablePostamble(targetSaved?.postamble ?? "");
    preEditSnapshotRef.current = null;
    preEditDiagnosticsRef.current = [];
  };

  // Returns the CodeMirror props that vary between report mode (full markdown
  // in the left panel) and normal code mode. Spread onto each split-view editor
  // so the 4 artifact types (react/plotly/mermaid/markmap) stay in sync.
  //   nativeLang      — lang key passed to getCmExtensions in normal mode
  //   supportsSelection — true for react/plotly which wire onUpdate for AI edit
  const getReportEditorProps = (nativeLang, supportsSelection = false) => ({
    value: reportMode ? editableRaw : (browsePreviewCode ?? editableCode),
    extensions: reportMode
      ? getCmExtensions("markdown", true, theme === "dark")
      : [...getCmExtensions(nativeLang, true, theme === "dark"), ...diffExt],
    editable: browsingVersion == null && !showDiffView,
    onChange: reportMode ? onRawChange : onEditorChange,
    onUpdate: (!reportMode && supportsSelection) ? onSelectionUpdate : undefined,
  });

  const errorDeltaCount = diffPending
    ? diagnostics.filter(d => d.severity === "error").length
      - preEditDiagnosticsRef.current.filter(d => d.severity === "error").length
    : 0;

  const showDiffView = diffPending || showSavedDiff;

  // Version history popover — anchored below the History button, portaled to body
  // so it doesn't get covered by iframes (which create their own stacking context).
  const versionHistoryPanel = (
    <AnchoredPanel
      anchorRef={historyAnchorRef}
      isOpen={showVersionHistory}
      onClose={() => setShowVersionHistory(false)}
      width={288}
      align="right"
      className="p-0 overflow-hidden"
    >
      <ArtifactVersionHistory
        versions={versions}
        isLoading={versionsLoading}
        browsingVersion={browsingVersion}
        isLoadingVersion={isLoadingVersion}
        pinnedVersion={pinnedVersion}
        onBrowse={handleBrowseVersion}
        onExitBrowse={handleExitBrowse}
        onRestore={handleRestoreVersion}
        onPin={(n) => pinVersion(n, chatOwnerId)}
        onClose={() => setShowVersionHistory(false)}
      />
    </AnchoredPanel>
  );

  // Shared header bar — only rendered for types with hasTabs.
  // Drag overlay: while the user is dragging the splitter we mount a transparent
  // div that covers the entire split container (including any iframe). This
  // prevents the iframe document from swallowing mousemove/mouseup events,
  // which would otherwise leave the splitter "stuck" tracking the cursor.
  // Reused across all artifact branches that have a split container.
  const dragOverlay = isDraggingSplit ? (
    <div
      className="absolute inset-0 z-50 cursor-col-resize"
      style={{ background: "transparent" }}
    />
  ) : null;

  const controlsHeader = caps.hasTabs ? (
    <div className="flex-shrink-0 flex flex-col border-b border-edge-gray-200 dark:border-edge-panel-surface bg-fill-white dark:bg-fill-page-surface-dark">
      {/* Row 1: preview/code toggle + multi-artifact pager + right-side actions */}
      <div className="flex items-center justify-between gap-2 px-2 py-1">
        <div className="flex items-center gap-2 min-w-0">
          <PreviewCodeToggle showCode={viewMode === "code"} onToggle={(toCode) => { setViewMode(toCode ? "code" : "preview"); }} />
          <MultiArtifactPager items={displayItems} activeIndex={clampedItemIndex} onSelect={setActiveItemIndex} />
        </div>
        {/* Right-side actions: regen status (while ghosted) + reset + save (code-only), run (heavy+code), copy + diff + download + fullscreen */}
        <div className="flex items-center gap-1">
          {isRemoteEditing && !isEditing && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] text-ink-primary dark:text-ink-accent bg-fill-hex-eef3ff dark:bg-fill-hex-1a2040 border border-edge-hex-c7d7fd dark:border-edge-hex-2a3a6a">
              <span className="w-1.5 h-1.5 rounded-full bg-fill-primary dark:bg-fill-accent animate-pulse" />
              Editing…
            </span>
          )}
          {regenStatusPill}
          {/* Phase 3 — change-count summary (e.g. "+3 / −1"), shown only after an
              inline AI edit completes and before the user accepts/rejects.
              Phase B — also shows pending hunk count. The badge is clickable and
              jumps to the first pending change so the user always has an obvious
              way to find what changed. */}
          {diffPending && inlineEditChangeCounts && (
            <button
              onClick={() => jumpToHunk(1)}
              disabled={pendingHunkCount === 0}
              title={pendingHunkCount > 0 ? "Jump to next change" : "All changes reviewed"}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-fill-gray-100 dark:bg-fill-panel-surface border border-edge-gray-200 dark:border-edge-toolbar-hover text-[11px] font-mono hover:bg-fill-gray-200 dark:hover:bg-fill-toolbar-hover transition-colors disabled:opacity-60 disabled:cursor-default"
            >
              {inlineEditChangeCounts.added > 0 && (
                <span className="text-ink-brand-openai font-semibold">+{inlineEditChangeCounts.added}</span>
              )}
              {inlineEditChangeCounts.removed > 0 && (
                <span className="text-ink-red-500 font-semibold">−{inlineEditChangeCounts.removed}</span>
              )}
              {inlineEditHunks.length > 0 && (
                <span className="text-ink-gray-500 dark:text-ink-gray-400 font-sans">
                  · {pendingHunkCount}/{inlineEditHunks.length}
                </span>
              )}
            </button>
          )}
          {diffPending && pendingHunkCount > 0 && (
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => jumpToHunk(-1)}
                title="Previous change ([)"
                className={NEBULA_BTN_CLS}
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => jumpToHunk(1)}
                title="Next change (])"
                className={NEBULA_BTN_CLS}
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {isDirty && <ResetButton onReset={resetEdits} disabled={diffPending} />}
          {isDirty && answerId && (
            <SaveButton status={saveStatus} onSave={handleSave} hasErrors={hasLintErrors} disabled={diffPending} />
          )}
          {viewMode === "code" && isCodeDirty && !diffPending && (
            <button
              onClick={() => setShowSavedDiff(v => !v)}
              title={showSavedDiff ? "Close changes diff" : "Show unsaved changes"}
              className={showSavedDiff ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
            >
              <GitCompare className="w-3.5 h-3.5" />
            </button>
          )}
          {caps.isHeavy && viewMode === "code" && (
            <button
              disabled={hasLintErrors || (editableCode === appliedPreviewCode && !previewHasError)}
              onClick={() => {
                const isRetry = editableCode === appliedPreviewCode;
                setAppliedPreviewCode(editableCode);
                setPreviewHasError(false);
                if (isRetry) {
                  if (displayArtifact.type === "react") setReactVizRunKey(k => k + 1);
                  else setIframePreviewKey(k => k + 1);
                }
              }}
              title={hasLintErrors ? "Fix errors before running" : (editableCode === appliedPreviewCode && !previewHasError) ? "No changes to run" : "Apply changes to preview"}
              className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium transition-opacity ${
                hasLintErrors || (editableCode === appliedPreviewCode && !previewHasError)
                  ? 'bg-fill-gray-300 dark:bg-fill-strong-border text-ink-gray-400 dark:text-ink-gray-500 cursor-not-allowed'
                  : 'bg-fill-ink dark:bg-fill-editor-surface text-ink-white hover:opacity-80'
              }`}
            >
              ▶ Run
            </button>
          )}
          <VizCopyButton
            copied={copied}
            onClick={() => copyToClipboard(editableCode)}
            tooltip="Copy"
          />
          {caps.canDiff && displayArtifact.type !== "text" && !browsingVersion && (
            <button
              onClick={() => {
                if (canvasEditMode) {
                  exitCanvasEditMode();
                } else {
                  // The preview iframe is always mounted (in both code and
                  // preview modes thanks to the split layout), so the WYSIWYG
                  // edit affordances are clickable from either view — no
                  // forced view switch needed.
                  enterCanvasEditMode();
                }
              }}
              title={canvasEditMode ? "Exit text edit" : "Edit text on canvas"}
              className={canvasEditMode ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {answerId && versions.length > 0 && (
            <button
              ref={historyAnchorRef}
              onClick={() => setShowVersionHistory(v => !v)}
              title={showVersionHistory ? "Close version history" : "Version history"}
              className={showVersionHistory ? NEBULA_BTN_ACTIVE_CLS : NEBULA_BTN_CLS}
            >
              <History className="w-3.5 h-3.5" />
            </button>
          )}
          {!isFullscreen && (
            <NebulaCanvasControls
              artifact={displayArtifact}
              canDiff={caps.canDiff}
              diffMode={diffMode}
              hasPrevious={caps.canDiff && !!previousArtifact && hasDiff(previousArtifact.content, activeArtifact?.content)}
              isFullscreen={isFullscreen}
              hasReportContent={hasReportContent}
              reportMode={reportMode}
              onToggleReport={() => setReportMode(v => !v)}
              onToggleDiff={() => setDiffMode(v => !v)}
              onFullscreen={enterFullscreen}
              onDownload={handleDownload}
              previewWidth={previewWidth}
              onPreviewWidth={setPreviewWidth}
            />
          )}
        </div>
      </div>
      {/* Row 2: AI edit prompt or Accept/Reject diff gate */}
      {(
        diffPending ? (
          <div className="flex items-center gap-2 px-3 py-2 border-t-2 border-edge-canvas-accent/40 dark:border-edge-canvas-accent/25 bg-fill-hex-eef6ff/60 dark:bg-fill-hex-111520">
            <span className="flex-1 min-w-0 text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate">
              Review AI changes
              <span className="ml-1.5 text-[10px] opacity-60">⌘⏎ accept · ⌘Z reject</span>
            </span>
            {errorDeltaCount !== 0 && (
              <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                errorDeltaCount > 0
                  ? "bg-fill-red-100 dark:bg-fill-red-900/30 text-ink-red-800 dark:text-ink-red-300"
                  : "bg-fill-green-100 dark:bg-fill-green-900/30 text-ink-green-800 dark:text-ink-green-300"
              }`}>
                {errorDeltaCount > 0 ? `+${errorDeltaCount}` : `${errorDeltaCount}`} errors
              </span>
            )}
            {/* Phase 4 — toggle inline diff highlights so user can read clean code */}
            <button
              onClick={() => setHideInlineDiff(v => !v)}
              title={hideInlineDiff ? "Show diff highlights" : "Hide diff highlights"}
              className="flex-shrink-0 text-[11px] px-2 py-1 rounded-md text-ink-gray-500 dark:text-ink-gray-400 hover:bg-fill-gray-100 dark:hover:bg-fill-panel-surface transition-colors"
            >
              {hideInlineDiff ? "Show diff" : "Hide diff"}
            </button>
            <button
              onClick={handleAcceptEdit}
              title="Accept AI edit (⌘⏎)"
              className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium bg-fill-brand-openai/10 dark:bg-fill-brand-openai/20 text-ink-brand-openai border border-edge-brand-openai/30 hover:bg-fill-brand-openai/25 transition-colors"
            >
              <Check className="w-3 h-3" /> Accept
            </button>
            {/* Phase 4 — Tweak: revert and refocus prompt with "Refine: …" prefix */}
            <button
              onClick={handleTweakEdit}
              title="Revert and refine the prompt"
              className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium bg-fill-canvas-accent/10 dark:bg-fill-canvas-accent/20 text-ink-canvas-accent border border-edge-canvas-accent/30 hover:bg-fill-canvas-accent/25 transition-colors"
            >
              <Wand2 className="w-3 h-3" /> Tweak
            </button>
            <button
              onClick={handleRejectEdit}
              title="Reject AI edit (⌘Z)"
              className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium bg-fill-red-50 dark:bg-fill-red-900/20 text-ink-red-800 border border-edge-red-200 dark:border-edge-red-800 hover:bg-fill-red-100 dark:hover:bg-fill-red-900/30 transition-colors dark:text-ink-red-300"
            >
              <X className="w-3 h-3" /> Reject
            </button>
          </div>
        ) : (
          <div className="flex flex-col border-t-2 border-edge-canvas-accent/40 dark:border-edge-canvas-accent/25 bg-fill-hex-eef6ff/60 dark:bg-fill-hex-111520">
            {/* Hidden file input — triggered by paperclip button */}
            <input
              ref={editFileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={onEditFileInputChange}
            />

            {/* Selection badge — shown when text is selected in the editor */}
            {editSelection && !isEditing && (
              <div className="flex items-center gap-1 px-2 pt-1.5">
                <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-fill-canvas-accent/10 text-ink-canvas-accent border border-edge-canvas-accent/30">
                  {/* Phase 5 #15 — show actual line range when known, fall back to count */}
                  {editSelection.startLine && editSelection.endLine
                    ? (editSelection.startLine === editSelection.endLine
                        ? `Editing line ${editSelection.startLine}`
                        : `Editing lines ${editSelection.startLine}–${editSelection.endLine}`)
                    : `${editSelection.lineCount} ${editSelection.lineCount === 1 ? "line" : "lines"} selected`}
                  <button
                    type="button"
                    onClick={() => { editorSelectionRef.current = null; setEditSelection(null); }}
                    className="ml-0.5 hover:text-ink-white transition-colors"
                    title="Clear selection scope"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              </div>
            )}
            {/* Attachment chips — shown when files are attached */}
            {editAttachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-2 pt-1.5">
                {editAttachments.map((file) => (
                  <FileUploadItem
                    key={file.file__path ?? file.name}
                    file={file}
                    progress={file.progress}
                    onRemove={() => removeEditFile(file)}
                  />
                ))}
              </div>
            )}
            {/* Composer card — single cohesive input surface that lights up on focus */}
            <div className="px-3 py-2">
              {/* Quick chips — above the input row so they never push the send button out of bounds */}
              {!isEditing && !editPrompt.trim() && (quickActions.length > 0 || fixErrorsPrompt) && (
                <div className="flex flex-wrap items-center gap-1 mb-1.5">
                  {fixErrorsPrompt && (
                    <button
                      type="button"
                      onClick={() => submitPromptNow(fixErrorsPrompt)}
                      title="Send the current lint errors to the AI to fix"
                      className="flex-shrink-0 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-fill-red-500/10 text-ink-red-500 border border-edge-red-500/30 hover:bg-fill-red-500/20 transition-colors"
                    >
                      <Wand2 className="w-2.5 h-2.5" />
                      Fix {diagnostics.filter(d => d.severity === "error").length} {diagnostics.filter(d => d.severity === "error").length === 1 ? "error" : "errors"}
                    </button>
                  )}
                  {quickActions.map((label) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => {
                        setEditPrompt(label);
                        setTimeout(() => editPromptInputRef.current?.focus(), 0);
                      }}
                      title={`Use prompt: "${label}"`}
                      className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-fill-white/70 dark:bg-fill-toolbar-surface text-ink-toolbar-accent dark:text-ink-toolbar-accent-hover border border-edge-toolbar-text dark:border-edge-toolbar-active-surface hover:bg-fill-canvas-accent/15 hover:text-ink-canvas-accent hover:border-edge-canvas-accent/40 transition-colors"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <div className="photon-editor-composer rounded-xl border border-edge-hex-c5ddf3 dark:border-edge-hex-263850 bg-fill-white dark:bg-fill-toolbar-surface shadow-sm transition-all duration-150 focus-within:border-edge-canvas-accent focus-within:ring-2 focus-within:ring-focus-canvas-accent/20 focus-within:shadow-md flex items-center gap-1.5 px-2 py-1">
                <EditorModelSelector
                  language={displayArtifact?.type}
                  chatId={chat_id}
                  onChange={setEditorModelId}
                />
                <button
                  type="button"
                  onClick={openEditFilePicker}
                  disabled={isEditing}
                  title="Attach file"
                  className="flex-shrink-0 p-1.5 rounded-lg text-ink-gray-500 hover:text-ink-canvas-accent hover:bg-fill-canvas-accent/10 dark:text-ink-gray-400 dark:hover:text-ink-canvas-accent disabled:opacity-40 transition-colors"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <div className="w-px h-4 bg-fill-hex-c5ddf3 dark:bg-fill-hex-263850 flex-shrink-0" />
                {/* Textarea — flex-1 centre */}
                <textarea
                  ref={editPromptInputRef}
                  rows={1}
                  className="flex-1 min-w-0 text-[13px] px-1 py-0.5 bg-transparent border-none text-ink-ink dark:text-ink-white placeholder-ink-hex-90aabf dark:placeholder-ink-hex-4a6070 focus:outline-none focus:ring-0 disabled:opacity-50 resize-none overflow-hidden leading-relaxed"
                  style={{ minHeight: "28px", maxHeight: "140px", overflowY: editPrompt.includes("\n") ? "auto" : "hidden" }}
                  placeholder={
                    isEditing
                      ? "Working…"
                      : editSelection?.startLine
                        ? (editSelection.startLine === editSelection.endLine
                            ? `Describe a change to line ${editSelection.startLine}…`
                            : `Describe a change to lines ${editSelection.startLine}–${editSelection.endLine}…`)
                        : "Describe a change…"
                  }
                  value={editPrompt}
                  disabled={isEditing}
                  onChange={(e) => {
                    setEditPrompt(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px";
                  }}
                  onPaste={onEditPaste}
                  onKeyDown={(e) => {
                    const isDesktop = window.innerWidth >= 768;
                    if (isDesktop && e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleEditSubmit();
                    }
                  }}
                />
                {/* Error indicator — compact, right of textarea */}
                {editError && !editApplyFailure && (
                  <span className="flex-shrink-0 text-[11px] text-ink-red-400 max-w-[120px] truncate" title={editError}>
                    {editError}
                  </span>
                )}
                {/* Send / Stop */}
                {isEditing ? (
                  <button
                    onClick={handleCancelEdit}
                    title="Stop generation · Esc"
                    aria-label="Stop AI edit"
                    className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-fill-red-500/10 hover:bg-fill-red-500/20 text-ink-red-400 transition-colors animate-pulse"
                  >
                    <Square className="w-3 h-3 fill-current" />
                  </button>
                ) : (
                  <button
                    onClick={handleEditSubmit}
                    disabled={!editPrompt.trim() && editAttachments.length === 0}
                    title="Apply AI edit"
                    aria-label="Apply AI edit"
                    className="photon-primary-action flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-button-primary text-button-on-primary shadow-sm transition-all hover:shadow-md hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-sm"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {/* Phase A — INTENT line: the model's one-sentence summary of what
                it's about to do. Appears as soon as the model emits it (well
                before the SEARCH/REPLACE blocks finish), so the user can catch
                a misunderstood prompt early. */}
            {isEditing && editIntent && (
              <div className="px-2 pb-1">
                <div className="text-[11px] text-ink-gray-700 dark:text-ink-gray-300 italic flex items-start gap-1.5">
                  <span className="text-ink-canvas-accent flex-shrink-0">›</span>
                  <span className="line-clamp-2">{editIntent}</span>
                </div>
              </div>
            )}
            {/* Phase 2 — streaming token preview: last ~160 chars of the model's
                output, shown only while a chunk has arrived. Lets the user catch
                a wrong direction in seconds instead of waiting for completion. */}
            {isEditing && editStreamTail && (
              <div className="px-2 pb-1">
                <div className="text-[10px] font-mono text-ink-gray-500 dark:text-ink-gray-400 bg-fill-gray-50 dark:bg-fill-hex-222426 border border-edge-gray-200 dark:border-edge-panel-surface rounded px-2 py-1 max-h-16 overflow-hidden whitespace-pre-wrap break-all">
                  <span className="opacity-60">…</span>{editStreamTail}<span className="inline-block w-1 h-3 ml-0.5 align-middle bg-fill-canvas-accent animate-pulse" />
                </div>
              </div>
            )}
            {/* Phase A — APPLY FAILURE panel: shown when the model responded but
                its SEARCH/REPLACE blocks could not be located in the current
                code. Offers a one-click retry that re-submits the same prompt
                with a hint to use larger SEARCH blocks. */}
            {editApplyFailure && (
              <div className="px-2 pb-2">
                <div className="text-xs bg-fill-amber-500/10 border border-edge-amber-500/30 rounded p-2 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-ink-amber-700 dark:text-ink-amber-300 font-medium leading-snug">
                      {editApplyFailure.message || "AI edit could not be applied."}
                    </div>
                    <button
                      onClick={() => setEditApplyFailure(null)}
                      className="text-ink-amber-700 dark:text-ink-amber-300 hover:opacity-70 flex-shrink-0"
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                  <div className="text-[10px] text-ink-amber-700/80 dark:text-ink-amber-300/80">
                    {editApplyFailure.reason === "no_blocks_found"
                      ? "The model didn't return any edit blocks. Try rephrasing or being more specific."
                      : `${editApplyFailure.failed_count ?? "Some"} edit region(s) couldn't be located in the current code. The file may have changed, or the model used inexact context.`}
                  </div>
                  <div className="flex gap-1.5 pt-0.5">
                    <button
                      onClick={() => {
                        const lastPrompt = lastEditPromptRef.current || editPrompt;
                        const hint = editApplyFailure.reason === "search_miss"
                          ? "Use larger, more unique SEARCH blocks. Each SEARCH must be a verbatim contiguous excerpt from the current file."
                          : "Return your changes as SEARCH/REPLACE blocks following the format strictly.";
                        setEditApplyFailure(null);
                        submitPromptNow(`${lastPrompt}\n\n[Retry hint: ${hint}]`);
                      }}
                      className="text-[11px] px-2 py-0.5 rounded bg-fill-amber-500/20 hover:bg-fill-amber-500/30 text-ink-amber-800 dark:text-ink-amber-200 transition-colors"
                    >
                      Retry
                    </button>
                    {editApplyFailure.raw_response && (
                      <details className="text-[10px] flex-1">
                        <summary className="cursor-pointer text-ink-amber-700/70 dark:text-ink-amber-300/70 hover:opacity-100">
                          View AI response
                        </summary>
                        <pre className="mt-1 p-1.5 bg-fill-gray-50 dark:bg-fill-hex-0f1011 border border-edge-gray-200 dark:border-edge-panel-surface rounded max-h-32 overflow-auto whitespace-pre-wrap break-all font-mono text-[10px] text-ink-gray-700 dark:text-ink-gray-300">
                          {editApplyFailure.raw_response}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </div>
  ) : null;

  // ── Plain text response ─────────────────────────────────────────────────────
  if (displayArtifact.type === "text") {
    const { cleanedText } = transformContent(displayArtifact.content);

    // Still in research phase — all content stripped, nothing to show yet
    // (skip spinner when ghosted — the overlay on the previous artifact serves this purpose)
    if (!cleanedText && isLoading && !isGhosted) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 min-h-0">
          <div className="text-4xl select-none animate-pulse">◈</div>
          <p
            className="text-sm text-ink-canvas-accent dark:text-ink-canvas-accent text-center max-w-xs leading-relaxed italic font-medium"
            style={{
              opacity: titleVisible ? 1 : 0,
              transition: "opacity 0.25s ease",
              minHeight: "1.5em",
            }}
          >
            {displayTitle}
            <span className="beaming-dots">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </span>
          </p>
        </div>
      );
    }

    return (
      <div
        className={`relative flex-1 flex flex-col min-w-0 min-h-0 transition-opacity opacity-100 ${swapClass} ${isFullscreen ? "bg-fill-white dark:bg-fill-hex-0d1117" : ""}`}
      >
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {viewMode === "code" ? (
          <div ref={splitContainerRef} className="relative flex flex-1 min-h-0 overflow-hidden select-none">
            {dragOverlay}
            <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
              <CodeMirror
                value={browsePreviewCode ?? editableCode}
                theme={cmTheme}
                extensions={[...getCmExtensions("markdown", true, theme === "dark"), ...diffExt]}
                height="100%"
                editable={browsingVersion == null && !showDiffView}
                onChange={onEditorChange}
                onCreateEditor={handleEditorMount}
                onUpdate={onSelectionUpdate}
                basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: false, closeBrackets: false }}
                style={{ fontSize: "13px", height: "100%" }}
                className="h-full"
              />
            </div>
            <SplitDragHandle onMouseDown={onDragStart} />
            <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto py-8">
              <div className="max-w-4xl w-full mx-auto px-8 text-[14px] leading-7 text-ink-hex-3d4f5c dark:text-ink-hex-c8d8e4">
                <NebulaPreviewGate isVisible={isVisible}>
                  <MarkdownRenderer content={visiblePreviewCode} />
                </NebulaPreviewGate>
              </div>
            </div>
          </div>
        ) : (
          <div ref={previewRef} className={`flex-1 overflow-y-auto py-8`}>
            <div className="max-w-4xl w-full mx-auto px-8 text-[14px] leading-7 text-ink-hex-3d4f5c dark:text-ink-hex-c8d8e4">
              <NebulaPreviewGate isVisible={isVisible}>
                <MarkdownRenderer content={browsePreviewCode ? transformContent(visiblePreviewCode).cleanedText : cleanedText} />
              </NebulaPreviewGate>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── HTML artifact ───────────────────────────────────────────────────────────
  if (displayArtifact.type === "html") {
    return (
      <div
        className={`relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden bg-fill-white dark:bg-fill-page-surface-dark transition-opacity opacity-100 ${swapClass}`}
      >
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {/* P6 — AI edit popover for non-text element clicks in canvas edit mode */}
        {aiPopover && (
          <div
            style={{ position: 'fixed', left: aiPopover.x + 8, top: aiPopover.y + 8, zIndex: 9999 }}
            className="flex flex-col gap-1.5 bg-fill-white dark:bg-fill-hex-1e2022 border border-edge-gray-200 dark:border-edge-toolbar-hover rounded-lg shadow-lg p-2.5 min-w-[160px] max-w-[260px]"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-ink-ink dark:text-ink-white">Edit with AI</span>
              <button
                onClick={() => setAiPopover(null)}
                className="flex-shrink-0 text-ink-gray-400 hover:text-ink-gray-500 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <p className="text-[10px] text-ink-gray-500 dark:text-ink-gray-400 font-mono break-all line-clamp-2 select-text">
              {aiPopover.outerHtml.slice(0, 120)}
            </p>
            <button
              onClick={handleAiPopoverAccept}
              className="photon-primary-action flex items-center justify-center gap-1.5 text-[11px] px-2 py-1 rounded-md font-medium bg-button-primary text-button-on-primary transition-colors"
            >
              <Wand2 className="w-3 h-3" /> Edit with AI
            </button>
          </div>
        )}
        <div ref={previewRef} className="flex-1 min-h-0 overflow-hidden">
          {diffMode && diffLines.length > 0 ? (
            /* Unified diff view */
            <NebulaPreviewGate isVisible={isVisible}>
              <div className="w-full h-full overflow-auto bg-fill-hex-0d1117 font-mono text-xs leading-5 p-4">
                {diffLines.map((line, i) => (
                  <div
                    key={i}
                    className={`px-2 whitespace-pre-wrap break-all ${
                      line.type === "added"
                        ? "bg-fill-hex-0d2a1a text-ink-hex-3fb950"
                        : line.type === "removed"
                        ? "bg-fill-hex-2a0d0d text-ink-hex-f85149"
                        : "text-ink-hex-8b9eb0"
                    }`}
                  >
                    <span className="select-none mr-2 opacity-50">
                      {line.type === "added" ? "+" : line.type === "removed" ? "-" : " "}
                    </span>
                    {line.value}
                  </div>
                ))}
              </div>
            </NebulaPreviewGate>
          ) : (
            /* VS Code-style split: editor (in code mode) + iframe preview.
               Iframe is always mounted so its DOM / canvas-edit injection
               survive view toggles; only the editor pane is conditional. */
            <div ref={splitContainerRef} className="relative flex w-full h-full overflow-hidden select-none">
              {dragOverlay}
              {viewMode === "code" && (
                <>
                  <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
                    <CodeMirror
                      value={browsePreviewCode ?? editableCode}
                      theme={cmTheme}
                      extensions={[...getCmExtensions("html", true, theme === "dark"), ...diffExt]}
                      height="100%"
                      editable={browsingVersion == null && !showDiffView}
                      onChange={onEditorChange}
                      onCreateEditor={handleEditorMount}
                      onUpdate={onSelectionUpdate}
                      basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: true, closeBrackets: true }}
                      style={{ fontSize: "13px", height: "100%" }}
                      className="h-full"
                    />
                  </div>
                  <SplitDragHandle onMouseDown={onDragStart} />
                </>
              )}
              <div className="flex-1 min-h-0 flex justify-center overflow-auto bg-fill-gray-100 dark:bg-fill-hex-111111">
                <div
                  className="h-full transition-all duration-200"
                  style={{ width: previewWidth ? `${previewWidth}px` : '100%' }}
                >
                  <NebulaPreviewGate isVisible={isVisible}>
                    <iframe
                      ref={iframeRef}
                      // Key on answerId forces a fresh iframe (clean DOM + JS heap) for
                      // each new answer — prevents stale content lingering across
                      // regenerations even when srcDoc updates. iframePreviewKey is
                      // incremented on retry-run (same code) to force a fresh reload.
                      key={(answerId ?? "nebula-iframe") + "-" + iframePreviewKey}
                      title="Nebula Canvas"
                      srcDoc={prepareHtml(tokenizedForPreview.taggedHtml)}
                      onLoad={handleIframeLoad}
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                      allowFullScreen
                    />
                  </NebulaPreviewGate>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Image artifact ──────────────────────────────────────────────────────────
  if (displayArtifact.type === "image") {
    return (
      <NebulaPreviewGate isVisible={isVisible}>
        <CanvasImage
          artifact={displayArtifact}
          items={displayItems}
          activeItemIndex={clampedItemIndex}
          onSelectItem={setActiveItemIndex}
          hasReportContent={hasReportContent}
          reportMode={reportMode}
          onToggleReport={() => setReportMode(v => !v)}
          preamble={editablePreamble}
          postamble={editablePostamble}
          isGhosted={isGhosted}
          ghostedOverlay={ghostedOverlay}
          changeBadgeOverlay={changeBadgeOverlay}
          statusPill={regenStatusPill}
          swapClass={swapClass}
          previewRef={previewRef}
          enterFullscreen={enterFullscreen}
          isFullscreen={isFullscreen}
          onDownload={handleDownload}
        />
      </NebulaPreviewGate>
    );
  }

  // ── Video artifact ──────────────────────────────────────────────────────────────────────
  if (displayArtifact.type === "video") {
    return (
      <NebulaPreviewGate isVisible={isVisible}>
        <CanvasVideo
          artifact={displayArtifact}
          items={displayItems}
          activeItemIndex={clampedItemIndex}
          onSelectItem={setActiveItemIndex}
          hasReportContent={hasReportContent}
          reportMode={reportMode}
          onToggleReport={() => setReportMode(v => !v)}
          preamble={editablePreamble}
          postamble={editablePostamble}
          isGhosted={isGhosted}
          ghostedOverlay={ghostedOverlay}
          changeBadgeOverlay={changeBadgeOverlay}
          statusPill={regenStatusPill}
          swapClass={swapClass}
          previewRef={previewRef}
          enterFullscreen={enterFullscreen}
          isFullscreen={isFullscreen}
          onDownload={handleDownload}
        />
      </NebulaPreviewGate>
    );
  }
  // ── Audio artifact ───────────────────────────────────────────────────────────────────
  if (displayArtifact.type === "audio") {
    return (
      <NebulaPreviewGate isVisible={isVisible}>
        <CanvasAudio
          artifact={displayArtifact}
          items={displayItems}
          activeItemIndex={clampedItemIndex}
          onSelectItem={setActiveItemIndex}
          hasReportContent={hasReportContent}
          reportMode={reportMode}
          onToggleReport={() => setReportMode(v => !v)}
          preamble={editablePreamble}
          postamble={editablePostamble}
          isGhosted={isGhosted}
          ghostedOverlay={ghostedOverlay}
          changeBadgeOverlay={changeBadgeOverlay}
          statusPill={regenStatusPill}
          swapClass={swapClass}
          previewRef={previewRef}
          enterFullscreen={enterFullscreen}
          isFullscreen={isFullscreen}
          onDownload={handleDownload}
        />
      </NebulaPreviewGate>
    );
  }
  // ── React / JSX component ───────────────────────────────────────────────────
  if (displayArtifact.type === "react") {
    return (
      <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden transition-opacity opacity-100 ${swapClass}`}>
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {viewMode === "code" ? (
          /* VS Code-style split: draggable divider between editor and live preview */
          <div ref={splitContainerRef} className="relative flex flex-1 min-h-0 overflow-hidden select-none">
            {dragOverlay}
            {/* Code editor — width controlled by drag */}
            <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
              <CodeMirror
                {...getReportEditorProps("react", true)}
                theme={cmTheme}
                height="100%"
                onCreateEditor={handleEditorMount}
                basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: true, closeBrackets: true }}
                style={{ fontSize: "13px", height: "100%" }}
                className="h-full"
              />
            </div>
            <SplitDragHandle onMouseDown={onDragStart} />
            {/* Preview panel — shows report or just the chart */}
            {reportMode ? (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto bg-fill-white dark:bg-fill-hex-0d1117">
                <ReportTextBlock content={editablePreamble} position="pre" />
                <div className="px-4 py-4">
                  <NebulaPreviewGate isVisible={isVisible}>
                    <ReactViz key={reactVizRunKey} code={visiblePreviewCode} hideControls onError={browsePreviewCode == null ? setPreviewHasError : undefined} />
                  </NebulaPreviewGate>
                </div>
                <ReportTextBlock content={editablePostamble} position="post" />
              </div>
            ) : (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-auto bg-fill-white dark:bg-fill-hex-0d1117 flex justify-center">
                <div
                  className="h-full transition-all duration-200"
                  style={{ width: previewWidth ? `${previewWidth}px` : '100%', padding: previewWidth ? '16px' : '16px' }}
                >
                  <NebulaPreviewGate isVisible={isVisible}>
                    <ReactViz key={reactVizRunKey} code={visiblePreviewCode} hideControls onError={browsePreviewCode == null ? setPreviewHasError : undefined} />
                  </NebulaPreviewGate>
                </div>
              </div>
            )}
          </div>
        ) : reportMode ? (
          /* Report mode — preamble above, artifact, postamble below */
          <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
            <ReportTextBlock content={editablePreamble} position="pre" />
            <div className="px-4 py-4">
              <NebulaPreviewGate isVisible={isVisible}>
                <ReactViz key={reactVizRunKey} code={visiblePreviewCode} hideControls onError={browsePreviewCode == null ? setPreviewHasError : undefined} />
              </NebulaPreviewGate>
            </div>
            <ReportTextBlock content={editablePostamble} position="post" />
          </div>
        ) : (
          /* Preview mode — shows last Run result */
          <div ref={previewRef} className="flex-1 min-h-0 overflow-auto flex justify-center">
            <div
              className="h-full transition-all duration-200"
              style={{ width: previewWidth ? `${previewWidth}px` : '100%' }}
            >
              <NebulaPreviewGate isVisible={isVisible}>
                <ReactViz key={reactVizRunKey} code={visiblePreviewCode} hideControls onError={browsePreviewCode == null ? setPreviewHasError : undefined} />
              </NebulaPreviewGate>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Plotly chart (physics-viz) ───────────────────────────────────────────────
  if (displayArtifact.type === "plotly") {
    return (
      <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden transition-opacity opacity-100 ${swapClass}`}>
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {viewMode === "code" ? (
          <div ref={splitContainerRef} className="relative flex flex-1 min-h-0 overflow-hidden select-none">
            {dragOverlay}
            <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
              <CodeMirror
                {...getReportEditorProps("plotly", true)}
                theme={cmTheme}
                height="100%"
                onCreateEditor={handleEditorMount}
                basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: true, closeBrackets: true }}
                style={{ fontSize: "13px", height: "100%" }}
                className="h-full"
              />
            </div>
            <SplitDragHandle onMouseDown={onDragStart} />
            {reportMode ? (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
                <ReportTextBlock content={editablePreamble} position="pre" />
                <div className="px-4 py-4">
                  <NebulaPreviewGate isVisible={isVisible}>
                    <PhysicsViz code={visiblePreviewCode} hideControls />
                  </NebulaPreviewGate>
                </div>
                <ReportTextBlock content={editablePostamble} position="post" />
              </div>
            ) : (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-auto">
                <NebulaPreviewGate isVisible={isVisible}>
                  <PhysicsViz code={visiblePreviewCode} hideControls />
                </NebulaPreviewGate>
              </div>
            )}
          </div>
        ) : reportMode ? (
          /* Report mode — preamble above, artifact, postamble below */
          <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
            <ReportTextBlock content={editablePreamble} position="pre" />
            <div className="px-4 py-4">
              <NebulaPreviewGate isVisible={isVisible}>
                <PhysicsViz code={visiblePreviewCode} hideControls />
              </NebulaPreviewGate>
            </div>
            <ReportTextBlock content={editablePostamble} position="post" />
          </div>
        ) : (
          <div ref={previewRef} className="flex-1 min-h-0 overflow-auto">
            <NebulaPreviewGate isVisible={isVisible}>
              <PhysicsViz code={visiblePreviewCode} hideControls />
            </NebulaPreviewGate>
          </div>
        )}
      </div>
    );
  }

  // ── Mermaid diagram ──────────────────────────────────────────────────────────
  if (displayArtifact.type === "mermaid") {
    return (
      <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden transition-opacity opacity-100 ${swapClass}`}>
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {viewMode === "code" ? (
          <div ref={splitContainerRef} className="relative flex flex-1 min-h-0 overflow-hidden select-none">
            {dragOverlay}
            <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
              <CodeMirror
                {...getReportEditorProps("mermaid", true)}
                theme={cmTheme}
                height="100%"
                onCreateEditor={handleEditorMount}
                basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: true, closeBrackets: true }}
                style={{ fontSize: "13px", height: "100%" }}
                className="h-full"
              />
            </div>
            <SplitDragHandle onMouseDown={onDragStart} />
            {reportMode ? (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
                <ReportTextBlock content={editablePreamble} position="pre" />
                <div className="flex items-center justify-center p-8">
                  <NebulaPreviewGate isVisible={isVisible}>
                    <MermaidDiagram chart={visiblePreviewCode} hideControls />
                  </NebulaPreviewGate>
                </div>
                <ReportTextBlock content={editablePostamble} position="post" />
              </div>
            ) : (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-8">
                <NebulaPreviewGate isVisible={isVisible}>
                  <MermaidDiagram chart={visiblePreviewCode} hideControls />
                </NebulaPreviewGate>
              </div>
            )}
          </div>
        ) : reportMode ? (
          /* Report mode — preamble above, artifact, postamble below */
          <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
            <ReportTextBlock content={editablePreamble} position="pre" />
            <div className="flex items-center justify-center p-8">
              <NebulaPreviewGate isVisible={isVisible}>
                <MermaidDiagram chart={visiblePreviewCode} hideControls />
              </NebulaPreviewGate>
            </div>
            <ReportTextBlock content={editablePostamble} position="post" />
          </div>
        ) : (
          <div ref={previewRef} className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-8">
            <NebulaPreviewGate isVisible={isVisible}>
              <MermaidDiagram chart={visiblePreviewCode} hideControls />
            </NebulaPreviewGate>
          </div>
        )}
      </div>
    );
  }

  // ── Markmap mind map ──────────────────────────────────────────────────────────
  if (displayArtifact.type === "markmap") {
    return (
      <div className={`relative flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden transition-opacity opacity-100 ${swapClass}`}>
        {controlsHeader}
        {ghostedOverlay}{changeBadgeOverlay}
        {versionHistoryPanel}
        {viewMode === "code" ? (
          <div ref={splitContainerRef} className="relative flex flex-1 min-h-0 overflow-hidden select-none">
            {dragOverlay}
            <div style={{ width: editorWidth, minWidth: 200, flexShrink: 0 }} className="min-h-0 overflow-hidden">
              <CodeMirror
                {...getReportEditorProps("markmap", true)}
                theme={cmTheme}
                height="100%"
                onCreateEditor={handleEditorMount}
                basicSetup={{ lineNumbers: true, foldGutter: false, dropCursor: false, allowMultipleSelections: false, indentOnInput: true, syntaxHighlighting: true, bracketMatching: true, autocompletion: true, closeBrackets: true }}
                style={{ fontSize: "13px", height: "100%" }}
                className="h-full"
              />
            </div>
            <SplitDragHandle onMouseDown={onDragStart} />
            {reportMode ? (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
                <ReportTextBlock content={editablePreamble} position="pre" />
                <div className="flex items-center justify-center p-4">
                  <NebulaPreviewGate isVisible={isVisible}>
                    <MarkmapDiagram chart={visiblePreviewCode} hideControls />
                  </NebulaPreviewGate>
                </div>
                <ReportTextBlock content={editablePostamble} position="post" />
              </div>
            ) : (
              <div ref={previewRef} className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-4">
                <NebulaPreviewGate isVisible={isVisible}>
                  <MarkmapDiagram chart={visiblePreviewCode} hideControls />
                </NebulaPreviewGate>
              </div>
            )}
          </div>
        ) : reportMode ? (
          /* Report mode — preamble above, artifact, postamble below */
          <div ref={previewRef} className="flex-1 min-h-0 overflow-y-auto">
            <ReportTextBlock content={editablePreamble} position="pre" />
            <div className="flex items-center justify-center p-4">
              <NebulaPreviewGate isVisible={isVisible}>
                <MarkmapDiagram chart={visiblePreviewCode} hideControls />
              </NebulaPreviewGate>
            </div>
            <ReportTextBlock content={editablePostamble} position="post" />
          </div>
        ) : (
          <div ref={previewRef} className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-4">
            <NebulaPreviewGate isVisible={isVisible}>
              <MarkmapDiagram chart={visiblePreviewCode} hideControls />
            </NebulaPreviewGate>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default NebulaCanvas;
